import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  Plus, Search, Loader2, FileText, Clock, AlertTriangle,
  ChevronRight, Calendar, ArrowLeft,
  Package, RefreshCw, AlertCircle, Truck, Download,
  Upload, Factory, ClipboardCheck, FileUp, Sparkles, Settings2,
  Edit2, Info, Wand2, List, Eye, X, CheckCircle2, Save, Send,
  FolderOpen, Building, Building2, Pencil, Mail, Users
} from 'lucide-react';
import { WalkthroughTrigger } from '@/components/walkthrough';
import { AICommandModal, ScheduleRegister, ScheduleDetailView } from '@/components/AIScheduleWorkflow';

const STATUSES = ['Not Started', 'In Progress', 'Complete', 'Delayed', 'On Hold'];

const STATUS_COLORS = {
  'Not Started': 'bg-slate-100 text-slate-700',
  'not_ordered': 'bg-slate-100 text-slate-700',
  'Ordered': 'bg-blue-100 text-blue-700',
  'ordered': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-blue-100 text-blue-700',
  'In Production': 'bg-purple-100 text-purple-700',
  'in_production': 'bg-purple-100 text-purple-700',
  'Shipped': 'bg-cyan-100 text-cyan-700',
  'shipped': 'bg-cyan-100 text-cyan-700',
  'Received': 'bg-green-100 text-green-700',
  'received': 'bg-green-100 text-green-700',
  'delivered': 'bg-green-100 text-green-700',
  'Complete': 'bg-green-100 text-green-700',
  'Delayed': 'bg-red-100 text-red-700',
  'On Hold': 'bg-amber-100 text-amber-700',
  'on_hold': 'bg-amber-100 text-amber-700',
  'Cancelled': 'bg-red-50 text-red-500',
  'cancelled': 'bg-red-50 text-red-500',
};

const TRADE_LABELS = {
  structural_steel: 'Structural Steel',
  hvac: 'HVAC',
  electrical: 'Electrical',
  lifts_elevators: 'Lifts/Elevators',
  glazing_aluminium: 'Glazing & Aluminium',
  facade_cladding: 'Facade/Cladding',
  fire_protection: 'Fire Protection',
  precast_concrete: 'Precast Concrete',
  kitchen_equipment: 'Kitchen Equipment',
  plumbing: 'Plumbing',
  roofing: 'Roofing',
  carpentry_joinery: 'Carpentry/Joinery',
  flooring_tiling: 'Flooring/Tiling',
  painting: 'Painting',
  plastering: 'Plastering',
  brickwork_masonry: 'Brickwork/Masonry',
  landscaping: 'Landscaping',
  external_works: 'External Works',
  general: 'General'
};

// Normalize status for display
const normalizeStatus = (status) => {
  if (!status) return 'Not Started';
  const map = {
    'not_ordered': 'Not Started',
    'not_started': 'Not Started',
    'in_progress': 'In Progress',
    'in_production': 'In Production',
    'on_hold': 'On Hold',
    'delivered': 'Received',
    'shipped': 'Shipped',
    'ordered': 'Ordered',
    'cancelled': 'Cancelled',
  };
  return map[status.toLowerCase()] || map[status] || status;
};

const ProcurementSchedulePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = window.location;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [subcontractors, setSubcontractors] = useState([]);
  const [projectName, setProjectName] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [programmeFilter, setProgrammeFilter] = useState('');
  const [longLeadFilter, setLongLeadFilter] = useState(false);
  const [criticalFilter, setCriticalFilter] = useState(false);
  const [delayedFilter, setDelayedFilter] = useState(false);
  const [shopDrawingsFilter, setShopDrawingsFilter] = useState(false);

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newItem, setNewItem] = useState({
    package_name: '',
    description: '',
    subcontractor: '',
    subcontractor_id: '',
    supplier: '',
    is_long_lead: false,
    is_critical: false,
    requires_shop_drawings: false,
    enquiry_issue_planned: '',
    tender_return_planned: '',
    adjudication_planned: '',
    appointment_planned: '',
    delivery_planned: '',
    installation_complete_planned: '',
    notes: '',
    tags: []
  });

  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editingDate, setEditingDate] = useState(null);
  const [dateValue, setDateValue] = useState('');
  const [dateReason, setDateReason] = useState('');
  
  // Contractor editing
  const [editingContractor, setEditingContractor] = useState(false);
  const [contractorName, setContractorName] = useState('');
  const [contractorContact, setContractorContact] = useState('');
  const [contractorEmail, setContractorEmail] = useState('');
  const [contractorPhone, setContractorPhone] = useState('');
  const [savingContractor, setSavingContractor] = useState(false);
  const [companiesDropdown, setCompaniesDropdown] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  
  // Shop Drawing confirmation
  const [showShopDrawingConfirm, setShowShopDrawingConfirm] = useState(false);
  const [togglingShopDrawings, setTogglingShopDrawings] = useState(false);
  
  // Schedule subcontractor linking
  const [scheduleSubSearch, setScheduleSubSearch] = useState('');
  const [scheduleSubDropdownOpen, setScheduleSubDropdownOpen] = useState(false);
  const [scheduleSubCompanies, setScheduleSubCompanies] = useState([]);
  const [linkingSubcontractor, setLinkingSubcontractor] = useState(false);
  
  // Share dialog - RFI-style
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareRecipients, setShareRecipients] = useState([]);
  const [shareEmail, setShareEmail] = useState('');
  const [shareName, setShareName] = useState('');
  const [shareRole, setShareRole] = useState('');
  const [sharingInProgress, setSharingInProgress] = useState(false);
  const [shareSendType, setShareSendType] = useState('external');
  const [shareSendEmail, setShareSendEmail] = useState(true);
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);

  // Link existing shop drawing
  const [linkSDDialogOpen, setLinkSDDialogOpen] = useState(false);
  const [sdSearchQuery, setSDSearchQuery] = useState('');
  const [sdSearchResults, setSDSearchResults] = useState([]);
  const [linkingSD, setLinkingSD] = useState(false);

  // Clean schedule
  const [cleanDialogOpen, setCleanDialogOpen] = useState(false);
  const [cleanFlagged, setCleanFlagged] = useState([]);
  const [cleanSelected, setCleanSelected] = useState(new Set());
  const [cleanAnalyzing, setCleanAnalyzing] = useState(false);
  const [cleanRemoving, setCleanRemoving] = useState(false);

  // Share published items
  const [sharePublishedOpen, setSharePublishedOpen] = useState(false);
  const [sharePublishedItems, setSharePublishedItems] = useState([]);
  const [sharePubRecipients, setSharePubRecipients] = useState([]);
  const [sharePubName, setSharePubName] = useState('');
  const [sharePubEmail, setSharePubEmail] = useState('');
  const [sharePubRole, setSharePubRole] = useState('');
  const [sharePubSendType, setSharePubSendType] = useState('external');
  const [sharePubSendEmail, setSharePubSendEmail] = useState(true);
  const [sharePubInProgress, setSharePubInProgress] = useState(false);

  // Programme selection (from Programs module)
  const [programmeUploads, setProgrammeUploads] = useState([]);
  const [selectedProgrammeId, setSelectedProgrammeId] = useState('');
  const [extracting, setExtracting] = useState(false);

  // AI Command
  const [aiCommand, setAiCommand] = useState('');
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiCommandModalOpen, setAiCommandModalOpen] = useState(false);
  const [activeView, setActiveView] = useState('items'); // 'items' | 'register' | 'register-detail'
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [pendingBgJobs, setPendingBgJobs] = useState([]);
  const [bgJobResult, setBgJobResult] = useState(null);
  const [markReceivedOpen, setMarkReceivedOpen] = useState(false);
  const [markReceivedItem, setMarkReceivedItem] = useState(null);
  const [receivedDate, setReceivedDate] = useState('');
  const [receivedNotes, setReceivedNotes] = useState('');
  const [receivedType, setReceivedType] = useState('received');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editItemData, setEditItemData] = useState({});
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newScheduleItem, setNewScheduleItem] = useState({ description: '', area_zone_level: '', activity_name: '', site_required_date: '', order_by_date: '', expected_delivery_date: '', status: 'not_ordered', lead_time_value: '', lead_time_unit: 'weeks' });

  // Grouping state - collapsed by default
  const [expandedProgrammes, setExpandedProgrammes] = useState({});
  const [expandedTrades, setExpandedTrades] = useState({});

  // Group items by Programme -> Trade (two-level hierarchy)
  const groupedProcItems = useMemo(() => {
    const groups = {};
    // Reverse lookup: find trade label from package_name
    const tradeLabelValues = Object.values(TRADE_LABELS);
    items.forEach(item => {
      const progId = item.programme_link?.programme_upload_id || item.programme_upload_id || '_ungrouped';
      const prog = programmeUploads.find(p => p.id === progId);
      const progName = prog?.title || (progId === '_ungrouped' ? 'Other Items' : 'Unknown Programme');
      if (!groups[progId]) {
        groups[progId] = { name: progName, trades: {}, count: 0 };
      }
      // Determine trade name with smart fallback
      let trade = TRADE_LABELS[item.trade_category] || item.trade_category || item.trade_package;
      if (!trade) {
        // Try to match package_name against known trade labels
        const pkgLower = (item.package_name || '').toLowerCase();
        trade = tradeLabelValues.find(t => pkgLower.includes(t.toLowerCase())) || item.package_name || 'General';
      }
      if (!groups[progId].trades[trade]) {
        groups[progId].trades[trade] = [];
      }
      groups[progId].trades[trade].push(item);
      groups[progId].count++;
    });
    return groups;
  }, [items, programmeUploads]);

  const toggleProgramme = (progId) => {
    setExpandedProgrammes(prev => ({ ...prev, [progId]: !prev[progId] }));
  };

  const toggleTrade = (progId, trade) => {
    const key = `${progId}__${trade}`;
    setExpandedTrades(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Lead Times Editor
  const [leadTimesDialogOpen, setLeadTimesDialogOpen] = useState(false);
  const [editingLeadTimes, setEditingLeadTimes] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      if (longLeadFilter) params.append('is_long_lead', 'true');
      if (criticalFilter) params.append('is_critical', 'true');
      if (delayedFilter) params.append('delay_flag', 'true');
      if (shopDrawingsFilter) params.append('requires_shop_drawings', 'true');
      if (programmeFilter) params.append('programme_upload_id', programmeFilter);

      const [itemsRes, statsRes, subsRes, projectRes, programsRes, legacyRes] = await Promise.all([
        api.get(`/projects/${projectId}/procurement?${params.toString()}`),
        api.get(`/projects/${projectId}/procurement/stats`),
        api.get(`/projects/${projectId}/subcontractors`).catch(() => ({ data: [] })),
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/programs`).catch(() => ({ data: { programs: [] } })),
        api.get(`/projects/${projectId}/procurement/programme-revisions`).catch(() => ({ data: [] }))
      ]);

      setItems(itemsRes.data.items || []);
      setTotal(itemsRes.data.total || 0);
      setStats(statsRes.data);
      setSubcontractors(subsRes.data || []);
      // Merge centralized programs + legacy uploads
      const central = (programsRes.data?.programs || []).map(p => ({
        id: p.id, title: p.title || p.filename, status: p.status,
        uploaded_at: p.created_at, items_count: p.items_count || 0,
      }));
      const legacy = (legacyRes.data || [])
        .filter(u => u.extraction_status === 'completed' || u.status === 'completed')
        .map(u => ({
          id: u.id, title: u.file_name || u.filename || 'Legacy Programme',
          status: 'completed', uploaded_at: u.upload_date,
          items_count: u.items_created || 0,
        }));
      const merged = [...central];
      legacy.forEach(l => { if (!merged.find(m => m.id === l.id)) merged.push(l); });
      setProgrammeUploads(merged);
      if (projectRes.data?.project?.name) {
        setProjectName(projectRes.data.project.name);
      } else if (projectRes.data?.name) {
        setProjectName(projectRes.data.name);
      }
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Failed to load procurement schedule');
    } finally {
      setLoading(false);
    }
  }, [projectId, search, statusFilter, programmeFilter, longLeadFilter, criticalFilter, delayedFilter, shopDrawingsFilter]);

  // Track acknowledged job IDs to prevent re-appearing
  const acknowledgedJobIds = useRef(new Set());

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check background jobs only once on mount
  useEffect(() => {
    api.get(`/projects/${projectId}/background-jobs?status=completed`)
      .then(res => {
        const jobs = (res.data?.jobs || [])
          .filter(j => j.params?.schedule_type === 'procurement' && j.status !== 'acknowledged' && !acknowledgedJobIds.current.has(j.id));
        setPendingBgJobs(jobs);
      })
      .catch(() => {});
  }, [projectId]);

  // Check for item parameter in URL and open that item
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const itemId = searchParams.get('item');
    
    if (itemId && items.length > 0 && !detailDialogOpen) {
      const item = items.find(i => i.id === itemId);
      if (item) {
        openDetail(item);
        // Clear the item parameter from URL
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [items, location.search, detailDialogOpen]);

  const handleReviewBgJob = async (jobId) => {
    try {
      const res = await api.get(`/background-jobs/${jobId}`);
      const job = res.data;
      if (job.status === 'completed' && job.result) {
        // Store job result with jobId for acknowledge after save
        setBgJobResult({ ...job.result, _bgJobId: jobId });
        setAiCommandModalOpen(true);
        setPendingBgJobs(prev => prev.filter(j => j.id !== jobId));
      }
    } catch (err) {
      toast.error('Failed to load background job result');
    }
  };

  // Open a saved schedule detail view
  const openScheduleDetail = async (schedule) => {
    try {
      const res = await api.get(`/projects/${projectId}/schedule/${schedule.id}`);
      setSelectedSchedule(res.data);
      setScheduleItems(res.data.items || []);
      setActiveView('register-detail');
    } catch (err) {
      toast.error('Failed to load schedule');
    }
  };

  const handlePublishSchedule = async () => {
    if (!selectedSchedule) return;
    try {
      const res = await api.put(`/projects/${projectId}/schedule/${selectedSchedule.id}/publish`);
      setSelectedSchedule(prev => ({ ...prev, status: 'published' }));
      toast.success(res.data?.message || 'Schedule published');
      // Reload procurement items list so promoted items appear
      loadData();
    } catch (err) {
      toast.error('Failed to publish schedule');
    }
  };

  const handleMarkReceivedProcurement = async () => {
    if (!markReceivedItem || !selectedSchedule) return;
    try {
      await api.post(`/projects/${projectId}/schedule/${selectedSchedule.id}/item/${markReceivedItem.id}/mark-received`, {
        actual_received_date: receivedDate || new Date().toISOString().split('T')[0],
        receipt_type: receivedType,
        notes: receivedNotes,
      });
      setScheduleItems(prev => prev.map(item =>
        item.id === markReceivedItem.id
          ? { ...item, status: receivedType, actual_received_date: receivedDate || new Date().toISOString().split('T')[0] }
          : item
      ));
      toast.success('Item marked as received');
      setMarkReceivedOpen(false);
      setMarkReceivedItem(null);
      setReceivedDate('');
      setReceivedNotes('');
    } catch (err) {
      toast.error('Failed to mark as received');
    }
  };

  const handleUpdateItemStatus = async (itemId, newStatus) => {
    if (!selectedSchedule) return;
    try {
      await api.put(`/projects/${projectId}/schedule/${selectedSchedule.id}/item/${itemId}`, {
        status: newStatus,
      });
      setScheduleItems(prev => prev.map(item =>
        item.id === itemId ? { ...item, status: newStatus } : item
      ));
      toast.success('Status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Schedule detail stats
  const scheduleStats = scheduleItems.reduce((acc, item) => {
    const s = item.status || 'pending';
    acc[s] = (acc[s] || 0) + 1;
    acc.total = (acc.total || 0) + 1;
    const dueDate = item.site_required_date || item.required_on_site_date;
    const terminalStatuses = ['received', 'delivered', 'cancelled', 'on_hold'];
    if (dueDate && new Date(dueDate) < new Date() && !terminalStatuses.includes(s)) {
      acc.late = (acc.late || 0) + 1;
    }
    return acc;
  }, { total: 0, pending: 0, received: 0, late: 0 });

  const handleDeleteItem = async (itemId) => {
    if (!selectedSchedule || !window.confirm('Delete this line item?')) return;
    try {
      await api.delete(`/projects/${projectId}/schedule/${selectedSchedule.id}/item/${itemId}`);
      setScheduleItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Item deleted');
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const handleStartEdit = (item) => {
    setEditingItemId(item.id);
    setEditItemData({
      description: item.description || '',
      area_zone_level: item.area_zone_level || item.area || '',
      activity_name: item.activity_name || item.linked_activity || '',
      site_required_date: item.site_required_date || item.required_on_site_date || '',
      order_by_date: item.order_placement_date || item.order_by_date || '',
      expected_delivery_date: item.expected_delivery_date || '',
      consultant_approval_by: item.consultant_approval_by || '',
      status: item.status || 'not_ordered',
      lead_time_value: item.lead_time_value || (item.total_lead_time_weeks ? parseFloat(String(item.total_lead_time_weeks).replace(/[^\d.]/g, '')) || '' : ''),
      lead_time_unit: item.lead_time_unit || (String(item.total_lead_time_weeks || '').toLowerCase().includes('day') ? 'days' : 'weeks'),
    });
  };

  const handleSaveEdit = async () => {
    if (!selectedSchedule || !editingItemId) return;
    try {
      // Auto-recalculate order_by_date and delivery_date based on lead time and site date
      const data = { ...editItemData };
      const siteDate = data.site_required_date;
      const ltVal = parseFloat(data.lead_time_value);
      const ltUnit = data.lead_time_unit || 'weeks';
      if (siteDate && ltVal > 0) {
        const site = new Date(siteDate);
        const totalDays = ltUnit === 'weeks' ? ltVal * 7 : ltVal;
        const orderBy = new Date(site);
        orderBy.setDate(orderBy.getDate() - totalDays);
        const delivery = new Date(site);
        delivery.setDate(delivery.getDate() - Math.max(Math.round(totalDays * 0.1), 5));
        data.order_by_date = orderBy.toISOString().split('T')[0];
        data.order_placement_date = data.order_by_date;
        data.expected_delivery_date = delivery.toISOString().split('T')[0];
      }
      await api.put(`/projects/${projectId}/schedule/${selectedSchedule.id}/item/${editingItemId}`, data);
      setScheduleItems(prev => prev.map(item =>
        item.id === editingItemId ? { ...item, ...data } : item
      ));
      setEditingItemId(null);
      setEditItemData({});
      toast.success('Item updated');
    } catch (err) {
      toast.error('Failed to save changes');
    }
  };

  const handleAddItem = async () => {
    if (!selectedSchedule || !newScheduleItem.description) {
      toast.error('Description is required');
      return;
    }
    try {
      const res = await api.post(`/projects/${projectId}/schedule/${selectedSchedule.id}/items`, {
        ...newScheduleItem,
        item_no: scheduleItems.length + 1,
      });
      const addedItem = res.data?.item || { ...newScheduleItem, id: res.data?.item_id || String(Date.now()), item_no: scheduleItems.length + 1 };
      setScheduleItems(prev => [...prev, addedItem]);
      setNewScheduleItem({ description: '', area_zone_level: '', activity_name: '', site_required_date: '', order_by_date: '', expected_delivery_date: '', status: 'not_ordered' });
      setAddItemOpen(false);
      toast.success('Item added');
    } catch (err) {
      toast.error('Failed to add item');
    }
  };

  const handleCreate = async () => {
    if (!newItem.package_name) {
      toast.error('Please enter a package name');
      return;
    }

    setCreating(true);
    try {
      await api.post(`/projects/${projectId}/procurement`, newItem);
      toast.success('Procurement item created');
      setCreateDialogOpen(false);
      setNewItem({
        package_name: '',
        description: '',
        subcontractor: '',
        subcontractor_id: '',
        supplier: '',
        is_long_lead: false,
        is_critical: false,
        requires_shop_drawings: false,
        enquiry_issue_planned: '',
        tender_return_planned: '',
        adjudication_planned: '',
        appointment_planned: '',
        delivery_planned: '',
        installation_complete_planned: '',
        notes: '',
        tags: []
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create procurement item');
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (item) => {
    setLoadingDetail(true);
    setDetailDialogOpen(true);
    try {
      const response = await api.get(`/procurement/${item.id}`);
      setSelectedItem(response.data);
    } catch (error) {
      toast.error('Failed to load item details');
      setDetailDialogOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDateUpdate = async () => {
    if (!editingDate || !selectedItem) return;

    try {
      const response = await api.post(`/procurement/${selectedItem.id}/update-date`, {
        phase: editingDate.phase,
        activity: editingDate.activity,
        date_type: editingDate.type,
        value: dateValue || null,
        reason: dateReason || null
      });
      setSelectedItem(response.data);
      setEditingDate(null);
      setDateValue('');
      setDateReason('');
      toast.success('Date updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update date');
    }
  };

  const startEditingContractor = () => {
    if (selectedItem) {
      setContractorName(selectedItem.subcontractor || selectedItem.supplier || '');
      setContractorContact(selectedItem.subcontractor_contact || '');
      setContractorEmail(selectedItem.subcontractor_email || '');
      setContractorPhone(selectedItem.subcontractor_phone || '');
      setCompanySearch(selectedItem.subcontractor || selectedItem.supplier || '');
      setEditingContractor(true);
      // Load companies dropdown
      api.get(`/projects/${projectId}/procurement/companies-dropdown`)
        .then(res => setCompaniesDropdown(res.data || []))
        .catch(() => setCompaniesDropdown([]));
    }
  };

  const selectCompanyFromDropdown = (company) => {
    setContractorName(company.company_name);
    setContractorContact(company.contact_person || '');
    setContractorEmail(company.email || '');
    setContractorPhone(company.phone || '');
    setCompanySearch(company.company_name);
    setShowCompanyDropdown(false);
  };

  const filteredCompanies = useMemo(() => {
    if (!companySearch) return companiesDropdown;
    return companiesDropdown.filter(c =>
      c.company_name?.toLowerCase().includes(companySearch.toLowerCase())
    );
  }, [companiesDropdown, companySearch]);

  const handleSaveContractor = async () => {
    if (!selectedItem) return;
    
    setSavingContractor(true);
    try {
      const response = await api.put(`/procurement/${selectedItem.id}`, {
        subcontractor: contractorName || null,
        subcontractor_contact: contractorContact || null,
        subcontractor_email: contractorEmail || null,
        subcontractor_phone: contractorPhone || null
      });
      setSelectedItem(response.data);
      setEditingContractor(false);
      toast.success('Contractor info updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update contractor info');
    } finally {
      setSavingContractor(false);
    }
  };

  const handleReExtract = async (uploadId) => {
    setExtracting(true);
    try {
      const res = await api.post(`/projects/${projectId}/procurement/extract-from-programme/${uploadId}`);
      
      if (res.data.extraction_status === 'completed') {
        // Already completed
        if (res.data.items_created > 0) {
          toast.success(`Created ${res.data.items_created} procurement items`);
          loadData();
        } else {
          toast.info('No new procurement activities found');
        }
        setExtracting(false);
        return;
      }
      
      if (res.data.extraction_status === 'processing') {
        // Poll for status
        toast.info('AI extraction started. This may take a moment...');
        pollExtractionStatus(uploadId);
      }
    } catch (error) {
      toast.error('Extraction failed');
      setExtracting(false);
    }
  };

  const pollExtractionStatus = async (uploadId) => {
    const maxAttempts = 60; // 2 minutes max
    let attempts = 0;
    
    const poll = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/procurement/extraction-status/${uploadId}`);
        const status = res.data.extraction_status;
        
        if (status === 'completed') {
          setExtracting(false);
          if (res.data.items_created > 0) {
            toast.success(`Created ${res.data.items_created} procurement items from ${res.data.activities_extracted || 0} activities`);
          } else {
            toast.info('No procurement activities found in the programme');
          }
          loadData();
          return;
        }
        
        if (status === 'failed') {
          setExtracting(false);
          toast.error(res.data.extraction_error || 'Extraction failed');
          return;
        }
        
        // Still processing
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else {
          setExtracting(false);
          toast.error('Extraction timed out. Please try again or check status later.');
        }
      } catch (error) {
        setExtracting(false);
        toast.error('Failed to check extraction status');
      }
    };
    
    poll();
  };

  const openLeadTimesEditor = (item) => {
    setEditingLeadTimes({
      item_id: item.id,
      package_name: item.package_name,
      trade_category: item.trade_category,
      lead_times: item.lead_times || {}
    });
    setLeadTimesDialogOpen(true);
  };

  const handleUpdateLeadTimes = async () => {
    if (!editingLeadTimes) return;

    try {
      await api.post('/procurement/update-lead-times', {
        item_id: editingLeadTimes.item_id,
        lead_times: editingLeadTimes.lead_times
      });
      toast.success('Lead times updated and dates recalculated');
      setLeadTimesDialogOpen(false);
      setEditingLeadTimes(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update lead times');
    }
  };

  // Create Shop Drawing Package
  const handleCreateShopDrawing = async () => {
    if (!selectedItem) return;

    try {
      const response = await api.post(`/procurement/${selectedItem.id}/create-shop-drawing-package`);
      
      if (response.data.already_exists) {
        toast.info('Shop drawing package already exists');
      } else {
        toast.success(
          <div>
            <p>Created shop drawing package: <strong>{response.data.package_ref}</strong></p>
            <button className="text-blue-600 underline text-xs mt-1" onClick={() => navigate(`/shop-drawings/${response.data.shop_drawing_id}`)}>
              Go to Package
            </button>
          </div>,
          { duration: 8000 }
        );
      }
      
      // Refresh the selected item
      const updatedItem = await api.get(`/procurement/${selectedItem.id}`);
      setSelectedItem(updatedItem.data);
      loadData();
    } catch (error) {
      toast.error('Failed to create shop drawing package');
    }
  };

  // Search existing shop drawings for linking
  const handleSearchShopDrawings = async (query) => {
    setSDSearchQuery(query);
    try {
      const res = await api.get(`/procurement/project/${projectId}/shop-drawings-search?q=${encodeURIComponent(query)}`);
      setSDSearchResults(res.data || []);
    } catch { setSDSearchResults([]); }
  };

  // Link existing shop drawing to procurement item
  const handleLinkExistingSD = async (sdId) => {
    if (!selectedItem) return;
    setLinkingSD(true);
    try {
      await api.post(`/procurement/${selectedItem.id}/link-shop-drawing`, { shop_drawing_id: sdId });
      const updatedItem = await api.get(`/procurement/${selectedItem.id}`);
      setSelectedItem(updatedItem.data);
      setLinkSDDialogOpen(false);
      toast.success('Shop drawing linked successfully');
      loadData();
    } catch (e) {
      toast.error('Failed to link shop drawing');
    } finally { setLinkingSD(false); }
  };

  // Sync shop drawing dates into procurement
  const handleSyncShopDrawingDates = async () => {
    if (!selectedItem) return;
    try {
      const res = await api.post(`/procurement/${selectedItem.id}/sync-shop-drawing-dates`);
      setSelectedItem(res.data);
      toast.success('Dates synced from shop drawing module');
      loadData();
    } catch { toast.error('Failed to sync dates'); }
  };

  // Load project team members for share dialog
  const loadProjectTeamForShare = async () => {
    try {
      // Load all users to group by role
      const usersRes = await api.get('/users');
      const allUsers = usersRes.data || (usersRes.data?.users) || [];

      const internalRoles = ['admin', 'director', 'project_director', 'project_manager',
        'contracts_manager', 'commercial_manager', 'quantity_surveyor', 'planner',
        'construction_manager', 'site_agent', 'site_manager', 'site_engineer', 'foreman',
        'procurement_manager', 'plant_manager', 'stores_manager', 'document_controller',
        'quality_manager', 'safety_officer', 'project_administrator', 'other',
        'contracts_director', 'qs', 'cm', 'hs_officer', 'project_admin'];
      const subcontractorRoles = ['subcontractor'];

      const teamList = [];
      for (const u of allUsers) {
        const role = (u.role || '').toLowerCase();
        const name = `${u.name || ''}${u.surname ? ' ' + u.surname : ''}`.trim();
        if (!u.email) continue;

        let category = 'internal';
        let displayRole = u.role || 'Team';
        if (role.startsWith('prof_') || ['professional_team', 'consultant', 'principal_agent'].includes(role)) {
          category = 'consultant';
          displayRole = u.company ? `${u.company} - Consultant` : 'Consultant';
        } else if (subcontractorRoles.includes(role)) {
          category = 'subcontractor';
          displayRole = u.company ? `${u.company} - Subcontractor` : 'Subcontractor';
        } else if (internalRoles.includes(role)) {
          category = 'internal';
          displayRole = role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        }

        teamList.push({
          name: name || u.email,
          email: u.email,
          role: displayRole,
          category,
          is_internal: category === 'internal',
          is_consultant: category === 'consultant',
          is_subcontractor: category === 'subcontractor',
        });
      }
      setProjectTeamMembers(teamList);
    } catch { setProjectTeamMembers([]); }
  };

  // Analyze schedule for off-trade items
  const handleAnalyzeSchedule = async () => {
    if (!selectedSchedule) return;
    setCleanAnalyzing(true);
    setCleanFlagged([]);
    setCleanSelected(new Set());
    try {
      const res = await api.post(`/projects/${projectId}/schedule/${selectedSchedule.id}/analyze-trade`);
      const flagged = res.data?.flagged || [];
      setCleanFlagged(flagged);
      setCleanSelected(new Set(flagged.map(f => f.id)));
      if (flagged.length === 0) {
        toast.success('All items match the trade — no cleanup needed!');
        setCleanDialogOpen(false);
      }
    } catch {
      toast.error('Failed to analyze schedule');
    } finally { setCleanAnalyzing(false); }
  };

  // Bulk remove flagged items
  const handleCleanRemove = async () => {
    if (!selectedSchedule || cleanSelected.size === 0) return;
    setCleanRemoving(true);
    try {
      const res = await api.post(`/projects/${projectId}/schedule/${selectedSchedule.id}/clean`, {
        item_ids: Array.from(cleanSelected)
      });
      toast.success(`Removed ${res.data?.removed || 0} off-trade items`);
      setCleanDialogOpen(false);
      setCleanFlagged([]);
      setCleanSelected(new Set());
      // Reload schedule items
      const updatedSchedule = await api.get(`/projects/${projectId}/schedule/${selectedSchedule.id}`);
      setSelectedSchedule(updatedSchedule.data);
      setScheduleItems(updatedSchedule.data.items || []);
      loadData();
    } catch {
      toast.error('Failed to remove items');
    } finally { setCleanRemoving(false); }
  };

  // Open share dialog for published items (individual or batch)
  const openSharePublished = (itemsToShare) => {
    setSharePublishedItems(itemsToShare);
    setSharePubRecipients([]);
    setSharePubSendType('external');
    setSharePubSendEmail(true);
    loadProjectTeamForShare();
    setSharePublishedOpen(true);
  };

  // Submit share for published items
  const handleSharePublished = async () => {
    if (sharePubRecipients.length === 0) return;
    setSharePubInProgress(true);
    try {
      // Build item summaries for email body
      const itemSummaries = sharePublishedItems.map(item => {
        const name = item.package_name || item.description || 'Item';
        const status = item.status || '-';
        return `  - ${name} (${status})`;
      }).join('\n');

      const packageNames = sharePublishedItems.map(i => i.package_name || i.description).join(', ');

      // Log the share action for each item
      for (const item of sharePublishedItems) {
        await api.post(`/procurement/${item.id}/share`, {
          recipients: sharePubRecipients.map(r => ({ name: r.name, email: r.email, role: r.role })),
          send_type: sharePubSendType,
        }).catch(() => {}); // Best effort per-item logging
      }

      if (sharePubSendEmail) {
        const toEmails = sharePubRecipients.map(r => r.email).join(',');
        const subject = encodeURIComponent(
          sharePublishedItems.length === 1
            ? `Procurement Schedule - ${sharePublishedItems[0].package_name}`
            : `Procurement Schedule - ${sharePublishedItems.length} Packages`
        );
        const body = encodeURIComponent(
          `Dear all,\n\nPlease find the Procurement Schedule details below:\n\n` +
          `Project: ${projectId}\n` +
          `Packages (${sharePublishedItems.length}):\n${itemSummaries}\n\n` +
          `View online: ${window.location.href}\n\n` +
          `Kind regards`
        );
        window.open(`mailto:${toEmails}?subject=${subject}&body=${body}`, '_blank');
      }

      toast.success(`Shared ${sharePublishedItems.length} package${sharePublishedItems.length !== 1 ? 's' : ''} with ${sharePubRecipients.length} recipients`);
      setSharePublishedOpen(false);
    } catch {
      toast.error('Failed to share packages');
    } finally { setSharePubInProgress(false); }
  };

  // AI Command Handler
  const handleAiCommand = async () => {
    if (!aiCommand.trim()) {
      toast.error('Please enter a command');
      return;
    }
    if (!selectedProgrammeId) {
      toast.error('Please select a programme first');
      return;
    }

    setAiProcessing(true);
    setAiResult(null);

    try {
      const response = await api.post(`/projects/${projectId}/procurement/ai-command`, {
        command: aiCommand,
        programme_upload_id: selectedProgrammeId
      });

      setAiResult(response.data);

      if (response.data.success) {
        const results = response.data.results || {};
        if (results.items_created > 0) {
          toast.success(`Created ${results.items_created} new procurement items`);
        } else if (results.items_updated > 0) {
          toast.success(`Updated ${results.items_updated} items`);
        } else if (results.activities_found === 0) {
          toast.warning('No matching activities found in the programme');
        } else {
          toast.info('Command processed');
        }
        loadData();
      } else {
        toast.error(response.data.error || 'Command failed');
      }
    } catch (error) {
      console.error('AI command error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process command');
    } finally {
      setAiProcessing(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/procurement/export`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `procurement_schedule_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export schedule');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setProgrammeFilter('');
    setLongLeadFilter(false);
    setCriticalFilter(false);
    setDelayedFilter(false);
    setShopDrawingsFilter(false);
  };

  const getPhaseStatus = (item, phaseName) => {
    const phase = item[phaseName] || {};
    const activities = Object.keys(phase);
    const completed = activities.filter(a => phase[a]?.actual).length;
    const total = activities.length;
    
    if (completed === 0) return 'not_started';
    if (completed === total) return 'complete';
    return 'in_progress';
  };

  const renderPhaseIndicator = (status) => {
    if (status === 'complete') {
      return <div className="w-3 h-3 rounded-full bg-green-500" title="Complete" />;
    }
    if (status === 'in_progress') {
      return <div className="w-3 h-3 rounded-full bg-blue-500" title="In Progress" />;
    }
    return <div className="w-3 h-3 rounded-full bg-slate-200" title="Not Started" />;
  };

  const renderDateCell = (phase, activity, label) => {
    if (!selectedItem) return null;
    const dateSet = selectedItem[phase]?.[activity] || {};
    
    return (
      <div className="border-b border-slate-100 py-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-700">{label}</span>
          {dateSet.actual && (
            <Badge className="bg-green-100 text-green-700 text-xs">Done</Badge>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-slate-400 block mb-1">Planned</span>
            <button
              className="flex items-center gap-1 text-slate-700 hover:text-blue-600 bg-slate-50 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
              onClick={() => {
                setEditingDate({ phase, activity, type: 'planned' });
                setDateValue(dateSet.planned || '');
              }}
              data-testid={`edit-${phase}-${activity}-planned`}
            >
              <Edit2 className="h-3 w-3" />
              {formatDate(dateSet.planned)}
            </button>
          </div>
          <div>
            <span className="text-slate-400 block mb-1">Forecast</span>
            <button
              className="flex items-center gap-1 text-slate-700 hover:text-blue-600 bg-slate-50 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
              onClick={() => {
                setEditingDate({ phase, activity, type: 'forecast' });
                setDateValue(dateSet.forecast || '');
              }}
              data-testid={`edit-${phase}-${activity}-forecast`}
            >
              <Edit2 className="h-3 w-3" />
              {formatDate(dateSet.forecast)}
            </button>
          </div>
          <div>
            <span className="text-slate-400 block mb-1">Actual</span>
            <button
              className="flex items-center gap-1 text-slate-700 hover:text-blue-600 bg-slate-50 px-2 py-1 rounded hover:bg-blue-50 transition-colors"
              onClick={() => {
                setEditingDate({ phase, activity, type: 'actual' });
                setDateValue(dateSet.actual || '');
              }}
              data-testid={`edit-${phase}-${activity}-actual`}
            >
              <Edit2 className="h-3 w-3" />
              {formatDate(dateSet.actual)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 min-w-0 overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(`/projects/${projectId}`)}
            className="text-slate-600 hover:text-slate-900 mb-3 -ml-2 text-sm"
            data-testid="back-to-project-btn"
          >
            ← Back to Project
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-barlow font-bold uppercase tracking-wider text-slate-900">
                Procurement Schedule
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                {projectName || 'Project'} - Track procurement & subcontractor timing
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <WalkthroughTrigger moduleId="procurement" />
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                className="rounded-sm hidden sm:flex"
                data-testid="export-btn"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden md:inline">Export</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  loadData();
                  api.get(`/projects/${projectId}/background-jobs?status=completed`)
                    .then(res => {
                      const jobs = (res.data?.jobs || [])
                        .filter(j => j.params?.schedule_type === 'procurement' && j.status !== 'acknowledged');
                      setPendingBgJobs(jobs);
                    })
                    .catch(() => {});
                }}
                disabled={loading}
                className="rounded-sm"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
                data-testid="create-item-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">New Item</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Background Job Completed Banner - hide when viewing a published schedule */}
        {pendingBgJobs.length > 0 && !(activeView === 'register-detail' && selectedSchedule?.status === 'published') && (
          <div className="space-y-2" data-testid="bg-jobs-banner">
            {pendingBgJobs.map(job => (
              <div key={job.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 sm:px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-1.5 bg-emerald-100 rounded-lg flex-shrink-0">
                    <Wand2 className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-emerald-900">AI Draft Ready</p>
                    <p className="text-xs text-emerald-700 truncate">
                      "{job.params?.command}"
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-9 sm:ml-0">
                  <Button
                    size="sm"
                    onClick={() => handleReviewBgJob(job.id)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    data-testid={`review-bg-job-${job.id}`}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> Review
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Acknowledge on backend so it doesn't come back
                      acknowledgedJobIds.current.add(job.id);
                      api.patch(`/background-jobs/${job.id}/acknowledge`).catch(() => {});
                      setPendingBgJobs(prev => prev.filter(j => j.id !== job.id));
                    }}
                    className="text-emerald-600 hover:text-emerald-800"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total_items}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-slate-500">Total</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.in_progress_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-blue-600">In Progress</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.completed_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-green-600">Complete</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.delayed_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-red-600">Delayed</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50 hidden sm:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats.long_lead_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-amber-600">Long Lead</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50 hidden lg:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-purple-600">{stats.critical_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-purple-600">Critical</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-indigo-200 bg-indigo-50/50 hidden lg:block">
              <CardContent className="p-3 sm:pt-4">
                <div className="text-center">
                  <p className="text-xl sm:text-2xl font-bold text-indigo-600">{stats.requires_shop_drawings_count}</p>
                  <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wider text-indigo-600">Shop Drgs</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* AI Command Section */}
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                <h3 className="font-bold text-sm sm:text-base text-slate-800">AI Schedule Assistant</h3>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={activeView === 'register' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setActiveView(activeView === 'register' ? 'items' : 'register')}
                  data-testid="toggle-register-btn"
                  className="text-xs sm:text-sm"
                >
                  <List className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">{activeView === 'register' ? 'Back to Items' : 'Saved Schedules'}</span>
                  <span className="sm:hidden">{activeView === 'register' ? 'Items' : 'Saved'}</span>
                </Button>
                <Button
                  onClick={() => setAiCommandModalOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                  size="sm"
                  data-testid="ai-command-btn"
                >
                  <Wand2 className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">AI Command</span>
                  <span className="sm:hidden">AI</span>
                </Button>
              </div>
            </div>
            
            {activeView === 'items' && (
              <div className="space-y-3">
                {/* Programme Selection */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 sm:max-w-[300px]">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">
                      Select Programme
                    </Label>
                    <Select 
                      value={selectedProgrammeId || "none"} 
                      onValueChange={(v) => {
                        const newId = v === "none" ? "" : v;
                        setSelectedProgrammeId(newId);
                        setProgrammeFilter(newId);
                      }}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="programme-select">
                        <SelectValue placeholder="Select a programme..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" disabled>Select a programme...</SelectItem>
                        {programmeUploads.map(prog => (
                          <SelectItem key={prog.id} value={prog.id}>
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-slate-400" />
                              <span className="truncate">{prog.title}</span>
                              {prog.items_count > 0 && (
                                <Badge className="ml-2 rounded-sm text-xs bg-green-100 text-green-700">
                                  {prog.items_count} items
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* No programme warning */}
                {programmeUploads.length === 0 && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
                    <p className="text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      No programmes uploaded yet. Upload a programme in the <strong>Programs</strong> module first.
                    </p>
                  </div>
                )}

                {programmeUploads.length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="data-file-recommendation-procurement">
                    <p className="text-sm text-blue-700">
                      <Info className="h-4 w-4 inline mr-1" />
                      <strong>Tip:</strong> Click <strong>AI Command</strong> to generate a full procurement schedule from the programme. You can specify trades like HVAC, Electrical, Joinery, etc.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeView === 'register' && (
              <ScheduleRegister
                projectId={projectId}
                scheduleType="procurement"
                programmes={programmeUploads}
                onOpenSchedule={openScheduleDetail}
                onRefresh={loadData}
              />
            )}

            {activeView === 'register-detail' && selectedSchedule && (
              <div className="space-y-4">
                {/* Detail Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Button variant="outline" size="sm" className="text-xs flex-shrink-0" onClick={() => { setActiveView('register'); setSelectedSchedule(null); setScheduleItems([]); }}>
                      <ArrowLeft className="h-3.5 w-3.5 mr-1" /> All Schedules
                    </Button>
                    <div className="min-w-0">
                      <h3 className="font-bold text-sm text-slate-800 truncate">{selectedSchedule.schedule_name}</h3>
                      <p className="text-[10px] text-slate-500">Rev {selectedSchedule.revision} | {selectedSchedule.trade_package || 'All Trades'}</p>
                    </div>
                    <Badge variant={selectedSchedule.status === 'published' ? 'default' : 'secondary'} className="text-xs flex-shrink-0">
                      {selectedSchedule.status}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    {selectedSchedule.status !== 'published' ? (
                      <Button variant="outline" size="sm" className="text-xs border-green-300 text-green-700 hover:bg-green-50" onClick={handlePublishSchedule} data-testid="publish-schedule-btn">
                        <CheckCircle2 className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Publish</span>
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50" onClick={handlePublishSchedule} data-testid="resync-schedule-btn">
                        <RefreshCw className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Sync</span>
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="text-xs hidden sm:flex" onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/schedule/${selectedSchedule.id}/export?format=xlsx`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) throw new Error('Export failed');
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url;
                        a.download = `${selectedSchedule.schedule_name || 'Procurement'}_${new Date().toISOString().split('T')[0]}.xlsx`;
                        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                        toast.success('Excel downloaded');
                      } catch { toast.error('Export failed'); }
                    }} data-testid="export-procurement-xlsx">
                      <Download className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Excel</span>
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs hidden sm:flex" onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/schedule/${selectedSchedule.id}/export?format=pdf`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!res.ok) throw new Error('Export failed');
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a'); a.href = url;
                        a.download = `${selectedSchedule.schedule_name || 'Procurement'}_${new Date().toISOString().split('T')[0]}.pdf`;
                        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                        toast.success('PDF downloaded');
                      } catch { toast.error('Export failed'); }
                    }} data-testid="export-procurement-pdf">
                      <FileText className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">PDF</span>
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs hidden sm:flex" onClick={() => setShareDialogOpen(true)} data-testid="share-procurement-btn">
                      <Send className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Share</span>
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs hidden sm:flex text-amber-600 border-amber-300 hover:bg-amber-50" onClick={() => {
                      setCleanDialogOpen(true);
                      handleAnalyzeSchedule();
                    }} data-testid="clean-schedule-btn">
                      <AlertCircle className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Clean</span>
                    </Button>
                  </div>
                </div>

                {/* Schedule Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Trade</p>
                    <p className="text-sm font-medium truncate">{selectedSchedule.trade_package || selectedSchedule.schedule_name}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Items</p>
                    <p className="text-sm font-medium">{scheduleItems.length}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Received</p>
                    <p className="text-sm font-medium text-green-600">{scheduleStats.received || 0} / {scheduleStats.total}</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-2 sm:p-3">
                    <p className="text-[10px] text-slate-500 uppercase">Late</p>
                    <p className="text-sm font-medium text-red-600">{scheduleStats.late || 0}</p>
                  </CardContent></Card>
                </div>

                {/* Linked Subcontractor */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-sm">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Building2 className="h-4 w-4 text-slate-500 flex-shrink-0" />
                    <span className="text-xs font-bold text-slate-600 uppercase flex-shrink-0">Subcontractor:</span>
                    {selectedSchedule.linked_subcontractor ? (
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-slate-800">{selectedSchedule.linked_subcontractor.company_name}</span>
                        {selectedSchedule.linked_subcontractor.contact_person && (
                          <span className="text-xs text-slate-500 ml-2">({selectedSchedule.linked_subcontractor.contact_person})</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Not linked - assign a sub-contractor to this schedule</span>
                    )}
                  </div>
                  <div className="relative">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs rounded-sm"
                      onClick={() => {
                        if (!scheduleSubDropdownOpen) {
                          api.get(`/projects/${projectId}/procurement/companies-dropdown`)
                            .then(res => setScheduleSubCompanies(res.data || []))
                            .catch(() => setScheduleSubCompanies([]));
                        }
                        setScheduleSubDropdownOpen(!scheduleSubDropdownOpen);
                        setScheduleSubSearch('');
                      }}
                      data-testid="link-subcontractor-btn"
                    >
                      {selectedSchedule.linked_subcontractor ? 'Change' : 'Link'}
                    </Button>
                    {scheduleSubDropdownOpen && (
                      <div className="absolute right-0 top-9 z-50 w-[calc(100vw-4rem)] sm:w-72 bg-white border border-slate-200 rounded-sm shadow-lg" data-testid="schedule-sub-dropdown">
                        <div className="p-2 border-b">
                          <Input
                            value={scheduleSubSearch}
                            onChange={(e) => setScheduleSubSearch(e.target.value)}
                            placeholder="Search companies..."
                            className="h-7 text-xs"
                            autoFocus
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {scheduleSubCompanies
                            .filter(c => !scheduleSubSearch || c.company_name?.toLowerCase().includes(scheduleSubSearch.toLowerCase()))
                            .map((c, i) => (
                              <button
                                key={c.id || i}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-slate-100 last:border-0"
                                onClick={async () => {
                                  setLinkingSubcontractor(true);
                                  try {
                                    await api.put(`/projects/${projectId}/schedule/${selectedSchedule.id}/link-subcontractor`, {
                                      company_name: c.company_name,
                                      contact_person: c.contact_person,
                                      email: c.email,
                                      phone: c.phone,
                                      source: c.source,
                                    });
                                    setSelectedSchedule(prev => ({
                                      ...prev,
                                      linked_subcontractor: {
                                        company_name: c.company_name,
                                        contact_person: c.contact_person,
                                        email: c.email,
                                        phone: c.phone,
                                      }
                                    }));
                                    toast.success(`Linked ${c.company_name}`);
                                    setScheduleSubDropdownOpen(false);
                                  } catch { toast.error('Failed to link'); }
                                  finally { setLinkingSubcontractor(false); }
                                }}
                                disabled={linkingSubcontractor}
                              >
                                <p className="font-medium text-slate-800 text-xs">{c.company_name}</p>
                                <p className="text-[10px] text-slate-400">
                                  {c.contact_person && `${c.contact_person} `}
                                  {c.source === 'crm' ? '(CRM)' : '(Subcontractor)'}
                                  {c.trade && ` - ${c.trade}`}
                                </p>
                              </button>
                            ))}
                          {scheduleSubCompanies.filter(c => !scheduleSubSearch || c.company_name?.toLowerCase().includes(scheduleSubSearch.toLowerCase())).length === 0 && (
                            <p className="text-xs text-slate-400 text-center py-3">No companies found</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Items Table */}
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-slate-500">{scheduleItems.length} line items</p>
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setAddItemOpen(!addItemOpen)} data-testid="add-item-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
                  </Button>
                </div>

                {/* Add Item Form */}
                {addItemOpen && (
                  <div className="border rounded-lg p-3 mb-3 bg-slate-50 space-y-2">
                    <p className="text-xs font-bold text-slate-600 uppercase">New Line Item</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="col-span-2">
                        <Label className="text-[10px]">Description *</Label>
                        <Input value={newScheduleItem.description} onChange={(e) => setNewScheduleItem(p => ({ ...p, description: e.target.value }))} placeholder="Item description" className="h-8 text-xs" data-testid="new-item-description" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Area / Zone</Label>
                        <Input value={newScheduleItem.area_zone_level} onChange={(e) => setNewScheduleItem(p => ({ ...p, area_zone_level: e.target.value }))} placeholder="e.g. Block A" className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Activity</Label>
                        <Input value={newScheduleItem.activity_name} onChange={(e) => setNewScheduleItem(p => ({ ...p, activity_name: e.target.value }))} placeholder="Activity name" className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Site Required</Label>
                        <Input type="date" value={newScheduleItem.site_required_date} onChange={(e) => setNewScheduleItem(p => ({ ...p, site_required_date: e.target.value }))} className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Order By</Label>
                        <Input type="date" value={newScheduleItem.order_by_date} onChange={(e) => setNewScheduleItem(p => ({ ...p, order_by_date: e.target.value }))} className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Delivery Date</Label>
                        <Input type="date" value={newScheduleItem.expected_delivery_date} onChange={(e) => setNewScheduleItem(p => ({ ...p, expected_delivery_date: e.target.value }))} className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Lead Time</Label>
                        <div className="flex items-center gap-1">
                          <Input type="number" value={newScheduleItem.lead_time_value} onChange={(e) => setNewScheduleItem(p => ({ ...p, lead_time_value: e.target.value }))} placeholder="0" min="0" className="h-8 text-xs w-16" />
                          <Select value={newScheduleItem.lead_time_unit || 'weeks'} onValueChange={(val) => setNewScheduleItem(p => ({ ...p, lead_time_unit: val }))}>
                            <SelectTrigger className="h-8 text-[10px] w-16"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="weeks">wks</SelectItem>
                              <SelectItem value="days">days</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex items-end gap-1">
                        <Button size="sm" className="h-8 text-xs bg-slate-800" onClick={handleAddItem} data-testid="save-new-item-btn">
                          <Save className="h-3 w-3 mr-1" /> Save
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setAddItemOpen(false)}>Cancel</Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="border rounded-lg overflow-x-auto bg-white">
                  <table className="w-full text-sm min-w-[1000px]" data-testid="procurement-schedule-items-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-2 py-2 text-left text-[10px] font-bold text-slate-500 uppercase w-8">#</th>
                        <th className="px-2 py-2 text-left text-[10px] font-bold text-slate-500 uppercase" style={{ width: 'auto', minWidth: '100px', maxWidth: '220px' }}>Description</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-slate-500 uppercase w-20">Status</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-blue-600 uppercase w-[72px]">Enquiry</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-blue-600 uppercase w-[72px]">Tender</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-blue-600 uppercase w-[72px]">Appoint</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-purple-600 uppercase w-[72px]">SD Sub</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-purple-600 uppercase w-[72px]">SD Appr</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-amber-600 uppercase w-[72px]">Fab Start</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-amber-600 uppercase w-[72px]">Fab End</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-green-600 uppercase w-[72px]">Delivery</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-green-600 uppercase w-[72px]">Install</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-green-600 uppercase w-[72px]">Complete</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-slate-500 uppercase w-14">Lead</th>
                        <th className="px-2 py-2 text-center text-[10px] font-bold text-slate-500 uppercase w-16">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {scheduleItems.length === 0 ? (
                        <tr>
                          <td colSpan={15} className="px-4 py-8 text-center text-slate-400 text-sm">No items in this schedule</td>
                        </tr>
                      ) : (
                        scheduleItems.map((item, idx) => {
                          const proc = item.procurement_phase || {};
                          const sd = item.shop_drawing_phase || {};
                          const mfg = item.manufacturing_phase || {};
                          const dlv = item.delivery_phase || {};
                          const terminalStatuses = ['received', 'delivered', 'cancelled', 'on_hold'];
                          const isEditing = editingItemId === item.id;

                          if (isEditing) {
                            return (
                              <tr key={item.id || idx} className="bg-blue-50/30">
                                <td className="px-2 py-2 text-xs text-slate-400">{item.item_no || idx + 1}</td>
                                <td className="px-2 py-2"><Input value={editItemData.description || ''} onChange={(e) => setEditItemData(p => ({ ...p, description: e.target.value }))} className="h-7 text-xs" /></td>
                                <td className="px-2 py-2 text-center">
                                  <Select value={editItemData.status || 'not_ordered'} onValueChange={(val) => setEditItemData(p => ({ ...p, status: val }))}>
                                    <SelectTrigger className="h-7 text-[10px] w-24"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="not_ordered">Not Ordered</SelectItem>
                                      <SelectItem value="ordered">Ordered</SelectItem>
                                      <SelectItem value="in_production">In Production</SelectItem>
                                      <SelectItem value="shipped">Shipped</SelectItem>
                                      <SelectItem value="received">Received</SelectItem>
                                      <SelectItem value="on_hold">On Hold</SelectItem>
                                      <SelectItem value="cancelled">Cancelled</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td colSpan={10} className="px-2 py-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex items-center gap-1">
                                      <span className="text-[9px] text-slate-400">Site:</span>
                                      <Input type="date" value={editItemData.site_required_date || ''} onChange={(e) => setEditItemData(p => ({ ...p, site_required_date: e.target.value }))} className="h-7 text-[10px] w-28" />
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span className="text-[9px] text-slate-400">Lead:</span>
                                      <Input type="number" value={editItemData.lead_time_value || ''} onChange={(e) => setEditItemData(p => ({ ...p, lead_time_value: e.target.value }))} className="h-7 text-[10px] w-14" placeholder="0" min="0" />
                                      <Select value={editItemData.lead_time_unit || 'weeks'} onValueChange={(val) => setEditItemData(p => ({ ...p, lead_time_unit: val }))}>
                                        <SelectTrigger className="h-7 text-[10px] w-16"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="weeks">wks</SelectItem>
                                          <SelectItem value="days">days</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <label className="flex items-center gap-1 text-[10px] text-slate-500">
                                      <input type="checkbox" checked={editItemData.requires_shop_drawings || false} onChange={(e) => setEditItemData(p => ({ ...p, requires_shop_drawings: e.target.checked }))} className="h-3 w-3" /> SD
                                    </label>
                                  </div>
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-green-600" onClick={handleSaveEdit} title="Save"><Save className="h-3.5 w-3.5" /></Button>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400" onClick={() => { setEditingItemId(null); setEditItemData({}); }} title="Cancel"><X className="h-3.5 w-3.5" /></Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          return (
                            <tr key={item.id || idx} className={`hover:bg-slate-50 ${item.delay_flag ? 'bg-red-50/30' : ''}`} data-testid={`procurement-item-${item.id || idx}`}>
                              <td className="px-2 py-2 text-xs text-slate-400">{item.item_no || idx + 1}</td>
                              <td className="px-2 py-2">
                                <p className="text-xs font-medium text-slate-800 line-clamp-2">{item.description || item.package_name || '-'}</p>
                                {(item.area_zone_level || item.area) && <p className="text-[9px] text-slate-400 mt-0.5">{item.area_zone_level || item.area}</p>}
                              </td>
                              <td className="px-2 py-2 text-center">
                                <Select
                                  value={item.status || 'not_ordered'}
                                  onValueChange={(val) => handleUpdateItemStatus(item.id, val)}
                                >
                                  <SelectTrigger className={`h-6 text-[10px] w-24 border-0 px-1 ${
                                    item.status === 'delivered' || item.status === 'received' ? 'bg-green-100 text-green-700' :
                                    item.status === 'ordered' ? 'bg-blue-100 text-blue-700' :
                                    item.status === 'in_production' ? 'bg-purple-100 text-purple-700' :
                                    item.status === 'shipped' ? 'bg-cyan-100 text-cyan-700' :
                                    item.status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
                                    item.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                    'bg-slate-100 text-slate-700'
                                  }`} data-testid={`status-select-${item.id || idx}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="not_ordered">Not Ordered</SelectItem>
                                    <SelectItem value="ordered">Ordered</SelectItem>
                                    <SelectItem value="in_production">In Production</SelectItem>
                                    <SelectItem value="shipped">Shipped</SelectItem>
                                    <SelectItem value="received">Received</SelectItem>
                                    <SelectItem value="on_hold">On Hold</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(proc.enquiry_issue?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(proc.tender_return?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(proc.appointment?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(sd.submit?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(sd.approval?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(mfg.fabrication_start?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(mfg.fabrication_complete?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(dlv.delivery_to_site?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(dlv.installation_start?.planned)}</td>
                              <td className="px-2 py-1.5 text-[10px] text-center text-slate-600">{formatDate(dlv.installation_complete?.planned)}</td>
                              <td className="px-2 py-2 text-[10px] text-center text-slate-600" data-testid={`lead-time-${item.id || idx}`}>
                                {item.lead_time_value ? (
                                  <span className="font-medium">{item.lead_time_value}{item.lead_time_unit === 'days' ? 'd' : 'w'}</span>
                                ) : item.total_lead_time_weeks ? (
                                  <span className="font-medium">{String(item.total_lead_time_weeks).replace(/working\s*/gi, '').trim()}</span>
                                ) : '-'}
                              </td>
                              <td className="px-2 py-2 text-center">
                                <div className="flex items-center justify-center gap-0.5">
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800" title="Edit" onClick={() => handleStartEdit(item)} data-testid={`edit-item-${item.id || idx}`}>
                                    <Edit2 className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" title="Delete" onClick={() => handleDeleteItem(item.id)} data-testid={`delete-item-${item.id || idx}`}>
                                    <X className="h-3 w-3" />
                                  </Button>
                                  {!terminalStatuses.includes(item.status) && (
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-green-600 hover:text-green-800" title="Mark Received"
                                      onClick={() => { setMarkReceivedItem(item); setMarkReceivedOpen(true); }}
                                    >
                                      <CheckCircle2 className="h-3 w-3" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <AICommandModal
          open={aiCommandModalOpen}
          onClose={() => { setAiCommandModalOpen(false); setBgJobResult(null); }}
          projectId={projectId}
          programmes={programmeUploads}
          defaultType="procurement"
          onScheduleSaved={async (savedSchedule, jobId) => {
            const bgJobId = jobId || bgJobResult?._bgJobId;
            setBgJobResult(null);
            if (bgJobId) {
              acknowledgedJobIds.current.add(bgJobId);
              setPendingBgJobs(prev => prev.filter(j => j.id !== bgJobId));
              try { await api.patch(`/background-jobs/${bgJobId}/acknowledge`); } catch {}
            }
            loadData();
            setActiveView('register');
          }}
          backgroundJobResult={bgJobResult}
        />

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-3 sm:pt-4">
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="flex-1 min-w-0 sm:min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search packages..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 rounded-sm"
                    data-testid="search-input"
                  />
                </div>
              </div>

              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-full sm:w-[150px] rounded-sm" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Programme Filter */}
              <Select value={programmeFilter || "all"} onValueChange={(v) => {
                const newFilter = v === "all" ? "" : v;
                setProgrammeFilter(newFilter);
                setSelectedProgrammeId(newFilter);
              }}>
                <SelectTrigger className="w-full sm:w-[180px] rounded-sm" data-testid="programme-filter">
                  <SelectValue placeholder="Programme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programmes</SelectItem>
                  {programmeUploads.map(prog => (
                    <SelectItem key={prog.id} value={prog.id}>
                      {prog.title?.substring(0, 25) || 'Unknown'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant={longLeadFilter ? "default" : "outline"}
                  size="sm"
                  onClick={() => setLongLeadFilter(!longLeadFilter)}
                  className="rounded-sm"
                  data-testid="long-lead-filter"
                >
                  <Clock className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Long Lead</span>
                </Button>

                <Button
                  variant={criticalFilter ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCriticalFilter(!criticalFilter)}
                  className="rounded-sm"
                  data-testid="critical-filter"
                >
                  <AlertCircle className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Critical</span>
                </Button>

                <Button
                  variant={delayedFilter ? "default" : "outline"}
                  size="sm"
                  onClick={() => setDelayedFilter(!delayedFilter)}
                  className="rounded-sm"
                  data-testid="delayed-filter"
                >
                  <AlertTriangle className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Delayed</span>
                </Button>

                <Button
                  variant={shopDrawingsFilter ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShopDrawingsFilter(!shopDrawingsFilter)}
                  className="rounded-sm"
                  data-testid="shop-drawings-filter"
                >
                  <FileText className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Shop Drgs</span>
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="rounded-sm"
                >
                  Clear
                </Button>

                {items.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm text-blue-600 border-blue-300 hover:bg-blue-50"
                    onClick={() => openSharePublished(items)}
                    data-testid="share-all-published-btn"
                  >
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Share All ({items.length})
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table / Mobile Cards */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : !programmeFilter ? (
              <div className="text-center py-16">
                <FolderOpen className="h-14 w-14 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 font-medium text-base">Select a programme to view packages</p>
                <p className="text-sm text-slate-400 mt-1">Choose a programme from the filter above to see procurement items</p>
              </div>
            ) : items.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No procurement items found</p>
                <p className="text-sm text-slate-400 mt-1">No items match the selected programme. Try publishing a schedule for this programme.</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View - Grouped by Programme -> Trade */}
                <div className="sm:hidden" data-testid="procurement-grouped-mobile">
                  {Object.entries(groupedProcItems).map(([progId, progGroup]) => (
                    <div key={progId} className="border-b-2 border-slate-200 last:border-b-0" data-testid={`procurement-programme-group-${progId}`}>
                      <button
                        className="w-full flex items-center justify-between p-3 bg-blue-50/60 hover:bg-blue-100/60 transition-colors text-left"
                        onClick={() => toggleProgramme(progId)}
                        data-testid={`toggle-procurement-programme-${progId}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ChevronRight className={`h-4 w-4 text-blue-600 transition-transform duration-200 flex-shrink-0 ${expandedProgrammes[progId] ? 'rotate-90' : ''}`} />
                          <FolderOpen className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          <span className="font-bold text-sm text-slate-800 truncate">{progGroup.name}</span>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700 text-xs flex-shrink-0 ml-2">{progGroup.count} items</Badge>
                      </button>
                      {expandedProgrammes[progId] && (
                        <div className="pl-2">
                          {Object.entries(progGroup.trades).map(([trade, tradeItems]) => {
                            const tradeKey = `${progId}__${trade}`;
                            return (
                              <div key={trade} className="border-b border-slate-100 last:border-b-0">
                                <button
                                  className="w-full flex items-center justify-between p-2 pl-4 bg-amber-50/40 hover:bg-amber-100/40 transition-colors text-left"
                                  onClick={() => toggleTrade(progId, trade)}
                                  data-testid={`toggle-procurement-trade-${tradeKey}`}
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    <ChevronRight className={`h-3.5 w-3.5 text-amber-600 transition-transform duration-200 flex-shrink-0 ${expandedTrades[tradeKey] ? 'rotate-90' : ''}`} />
                                    <Building className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
                                    <span className="font-medium text-sm text-slate-700 truncate">{trade}</span>
                                  </div>
                                  <Badge className="bg-amber-100 text-amber-700 text-xs flex-shrink-0 ml-2">{tradeItems.length}</Badge>
                                </button>
                                {expandedTrades[tradeKey] && (
                                  <div className="divide-y divide-slate-100">
                                    {tradeItems.map((item) => (
                                      <div
                                        key={item.id}
                                        className={`p-3 pl-10 cursor-pointer hover:bg-slate-50 ${item.delay_flag ? 'bg-red-50/30' : ''}`}
                                        onClick={() => openDetail(item)}
                                        data-testid={`procurement-card-${item.package_name}`}
                                      >
                                        <div className="flex items-start justify-between mb-2">
                                          <div>
                                            <p className="font-medium text-slate-900">{item.package_name}</p>
                                            <p className="text-xs text-slate-500 mt-0.5">{item.subcontractor || item.supplier || '-'}</p>
                                          </div>
                                          <Badge className={`rounded-sm text-xs ${STATUS_COLORS[normalizeStatus(item.status)] || STATUS_COLORS[item.status] || ''}`}>
                                            {normalizeStatus(item.status)}
                                          </Badge>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                          {item.is_long_lead && <Badge className="rounded-sm text-xs bg-amber-100 text-amber-700">Long Lead</Badge>}
                                          {item.is_critical && <Badge className="rounded-sm text-xs bg-red-100 text-red-700">Critical</Badge>}
                                          {item.requires_shop_drawings && <Badge className="rounded-sm text-xs bg-indigo-100 text-indigo-700">Shop Drgs</Badge>}
                                          {item.delay_flag && <Badge className="rounded-sm text-xs bg-red-100 text-red-700">Delayed {item.delay_days}d</Badge>}
                                        </div>
                                        <div className="flex items-center gap-3 mt-3">
                                          <div className="flex items-center gap-1">
                                            {renderPhaseIndicator(getPhaseStatus(item, 'procurement_phase'))}
                                            <span className="text-[10px] text-slate-400">Proc</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            {renderPhaseIndicator(getPhaseStatus(item, 'shop_drawing_phase'))}
                                            <span className="text-[10px] text-slate-400">SD</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            {renderPhaseIndicator(getPhaseStatus(item, 'manufacturing_phase'))}
                                            <span className="text-[10px] text-slate-400">Mfg</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            {renderPhaseIndicator(getPhaseStatus(item, 'delivery_phase'))}
                                            <span className="text-[10px] text-slate-400">Del</span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop Table View - Grouped by Programme -> Trade */}
                <div className="hidden sm:block" data-testid="procurement-grouped-desktop">
                  <div className="overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
                    <Table className="min-w-[1400px]">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="font-bold sticky left-0 bg-slate-50 z-10 min-w-[160px]">Package</TableHead>
                          <TableHead className="font-bold min-w-[90px]">Status</TableHead>
                          <TableHead className="font-bold text-center min-w-[70px]">Flags</TableHead>
                          {/* Procurement Phase */}
                          <TableHead className="font-bold text-center bg-blue-50 min-w-[72px]">Enquiry</TableHead>
                          <TableHead className="font-bold text-center bg-blue-50 min-w-[72px]">Tender</TableHead>
                          <TableHead className="font-bold text-center bg-blue-50 min-w-[72px]">Appoint</TableHead>
                          {/* Shop Drawing Phase */}
                          <TableHead className="font-bold text-center bg-purple-50 min-w-[72px]">SD Submit</TableHead>
                          <TableHead className="font-bold text-center bg-purple-50 min-w-[72px]">SD Approve</TableHead>
                          {/* Manufacturing Phase */}
                          <TableHead className="font-bold text-center bg-amber-50 min-w-[72px]">Fab Start</TableHead>
                          <TableHead className="font-bold text-center bg-amber-50 min-w-[72px]">Fab End</TableHead>
                          {/* Delivery Phase */}
                          <TableHead className="font-bold text-center bg-green-50 min-w-[72px]">Delivery</TableHead>
                          <TableHead className="font-bold text-center bg-green-50 min-w-[72px]">Install</TableHead>
                          <TableHead className="font-bold text-center bg-green-50 min-w-[72px]">Complete</TableHead>
                          <TableHead className="font-bold text-center min-w-[60px]">Delay</TableHead>
                          <TableHead className="font-bold text-center min-w-[50px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(groupedProcItems).map(([progId, progGroup]) => (
                          <React.Fragment key={progId}>
                            <TableRow
                              className="bg-blue-50/60 cursor-pointer hover:bg-blue-100/60 border-t-2 border-blue-200"
                              onClick={() => toggleProgramme(progId)}
                              data-testid={`procurement-programme-desktop-${progId}`}
                            >
                              <TableCell colSpan={15} className="py-2.5">
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center gap-2">
                                    <ChevronRight className={`h-4 w-4 text-blue-600 transition-transform duration-200 ${expandedProgrammes[progId] ? 'rotate-90' : ''}`} />
                                    <FolderOpen className="h-4 w-4 text-blue-600" />
                                    <span className="font-bold text-sm text-slate-800">{progGroup.name}</span>
                                  </div>
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">{progGroup.count} items</Badge>
                                </div>
                              </TableCell>
                            </TableRow>
                            {expandedProgrammes[progId] && Object.entries(progGroup.trades).map(([trade, tradeItems]) => {
                              const tradeKey = `${progId}__${trade}`;
                              return (
                                <React.Fragment key={trade}>
                                  <TableRow
                                    className="bg-amber-50/40 cursor-pointer hover:bg-amber-100/40"
                                    onClick={() => toggleTrade(progId, trade)}
                                    data-testid={`procurement-trade-desktop-${tradeKey}`}
                                  >
                                    <TableCell colSpan={15} className="py-2 pl-10">
                                      <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                          <ChevronRight className={`h-3.5 w-3.5 text-amber-600 transition-transform duration-200 ${expandedTrades[tradeKey] ? 'rotate-90' : ''}`} />
                                          <Building className="h-3.5 w-3.5 text-amber-600" />
                                          <span className="font-medium text-sm text-slate-700">{trade}</span>
                                        </div>
                                        <Badge className="bg-amber-100 text-amber-700 text-xs">{tradeItems.length}</Badge>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                  {expandedTrades[tradeKey] && tradeItems.map((item) => (
                                    <TableRow
                                      key={item.id}
                                      className={`cursor-pointer hover:bg-slate-50 ${item.delay_flag ? 'bg-red-50/30' : ''}`}
                                      onClick={() => openDetail(item)}
                                      data-testid={`procurement-row-${item.package_name}`}
                                    >
                                      <TableCell className="sticky left-0 bg-white z-10 pl-16">
                                        <div>
                                          <p className="font-medium text-slate-900">{item.package_name}</p>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <Badge className={`rounded-sm text-xs ${STATUS_COLORS[item.status] || STATUS_COLORS[normalizeStatus(item.status)] || ''}`}>
                                          {normalizeStatus(item.status)}
                                        </Badge>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center justify-center gap-1">
                                          {item.is_long_lead && (
                                            <div className="w-5 h-5 rounded bg-amber-100 flex items-center justify-center" title="Long Lead">
                                              <Clock className="h-3 w-3 text-amber-600" />
                                            </div>
                                          )}
                                          {item.is_critical && (
                                            <div className="w-5 h-5 rounded bg-red-100 flex items-center justify-center" title="Critical">
                                              <AlertCircle className="h-3 w-3 text-red-600" />
                                            </div>
                                          )}
                                          {item.requires_shop_drawings && (
                                            <div className="w-5 h-5 rounded bg-indigo-100 flex items-center justify-center" title="Shop Drawings Required">
                                              <FileText className="h-3 w-3 text-indigo-600" />
                                            </div>
                                          )}
                                        </div>
                                      </TableCell>
                                      {/* Procurement Phase Dates */}
                                      <TableCell className="text-xs text-center bg-blue-50/30">
                                        {formatDate(item.procurement_phase?.enquiry_issue?.planned)}
                                      </TableCell>
                                      <TableCell className="text-xs text-center bg-blue-50/30">
                                        {formatDate(item.procurement_phase?.tender_return?.planned)}
                                      </TableCell>
                                      <TableCell className="text-xs text-center bg-blue-50/30">
                                        {formatDate(item.procurement_phase?.appointment?.planned)}
                                      </TableCell>
                                      {/* Shop Drawing Phase Dates */}
                                      <TableCell className="text-xs text-center bg-purple-50/30">
                                        {formatDate(item.shop_drawing_phase?.submit?.planned)}
                                      </TableCell>
                                      <TableCell className="text-xs text-center bg-purple-50/30">
                                        {formatDate(item.shop_drawing_phase?.approval?.planned)}
                                      </TableCell>
                                      {/* Manufacturing Phase Dates */}
                                      <TableCell className="text-xs text-center bg-amber-50/30">
                                        {formatDate(item.manufacturing_phase?.fabrication_start?.planned)}
                                      </TableCell>
                                      <TableCell className="text-xs text-center bg-amber-50/30">
                                        {formatDate(item.manufacturing_phase?.fabrication_complete?.planned)}
                                      </TableCell>
                                      {/* Delivery Phase Dates */}
                                      <TableCell className="text-xs text-center bg-green-50/30">
                                        {formatDate(item.delivery_phase?.delivery_to_site?.planned)}
                                      </TableCell>
                                      <TableCell className="text-xs text-center bg-green-50/30">
                                        {formatDate(item.delivery_phase?.installation_start?.planned)}
                                      </TableCell>
                                      <TableCell className="text-xs text-center bg-green-50/30">
                                        {formatDate(item.delivery_phase?.installation_complete?.planned)}
                                      </TableCell>
                                      <TableCell>
                                        {item.delay_flag ? (
                                          <Badge className="rounded-sm text-xs bg-red-100 text-red-700">
                                            {item.delay_days}d
                                          </Badge>
                                        ) : (
                                          <span className="text-xs text-slate-400">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-1">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 hover:bg-blue-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openSharePublished([item]);
                                            }}
                                            data-testid={`share-item-${item.id}`}
                                            title="Share Package"
                                          >
                                            <Send className="h-3.5 w-3.5 text-blue-600" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7 hover:bg-blue-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openDetail(item);
                                            }}
                                            data-testid={`edit-item-${item.id}`}
                                            title="Edit Item"
                                          >
                                            <Edit2 className="h-4 w-4 text-blue-600" />
                                          </Button>
                                          <ChevronRight className="h-4 w-4 text-slate-400" />
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <p className="text-xs text-slate-400 text-center mt-2">
                    Scroll horizontally to see all phase dates
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        {stats?.upcoming_deadlines?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-amber-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Upcoming Deadlines (Next 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.upcoming_deadlines.map((deadline, idx) => (
                  <div key={idx} className="bg-white rounded-sm border border-amber-200 p-3">
                    <p className="font-medium text-slate-900 text-sm">{deadline.package_name}</p>
                    <p className="text-xs text-slate-500">{deadline.activity}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-slate-400">{formatDate(deadline.date)}</span>
                      <Badge className="rounded-sm text-xs bg-amber-100 text-amber-700">
                        {deadline.days_until === 0 ? 'Today' : `${deadline.days_until}d`}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                New Procurement Item
              </DialogTitle>
              <DialogDescription>
                Add a new trade package to the procurement schedule
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs font-bold uppercase tracking-wider">Package Name *</Label>
                  <Input
                    value={newItem.package_name}
                    onChange={(e) => setNewItem({ ...newItem, package_name: e.target.value })}
                    placeholder="e.g., Structural Steel, HVAC, Glazing"
                    className="mt-1 rounded-sm"
                    data-testid="new-package-name"
                  />
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs font-bold uppercase tracking-wider">Subcontractor</Label>
                  <Select
                    value={newItem.subcontractor_id || "none"}
                    onValueChange={(v) => {
                      if (v === "none") {
                        setNewItem({ ...newItem, subcontractor_id: '', subcontractor: '' });
                      } else {
                        const sub = subcontractors.find(s => s.id === v);
                        setNewItem({ 
                          ...newItem, 
                          subcontractor_id: v, 
                          subcontractor: sub?.company_name || '' 
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1 rounded-sm">
                      <SelectValue placeholder="Select subcontractor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None / Manual Entry</SelectItem>
                      {subcontractors.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {!newItem.subcontractor_id && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Subcontractor Name (Manual)</Label>
                  <Input
                    value={newItem.subcontractor}
                    onChange={(e) => setNewItem({ ...newItem, subcontractor: e.target.value })}
                    placeholder="Enter subcontractor name"
                    className="mt-1 rounded-sm"
                  />
                </div>
              )}

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Supplier</Label>
                <Input
                  value={newItem.supplier}
                  onChange={(e) => setNewItem({ ...newItem, supplier: e.target.value })}
                  placeholder="Material supplier (if different from subcontractor)"
                  className="mt-1 rounded-sm"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Description</Label>
                <Textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  placeholder="Scope and details..."
                  className="mt-1 rounded-sm"
                  rows={2}
                />
              </div>

              {/* Flags */}
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_long_lead"
                    checked={newItem.is_long_lead}
                    onCheckedChange={(checked) => setNewItem({ ...newItem, is_long_lead: checked })}
                  />
                  <Label htmlFor="is_long_lead" className="text-sm">Long Lead Item</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_critical"
                    checked={newItem.is_critical}
                    onCheckedChange={(checked) => setNewItem({ ...newItem, is_critical: checked })}
                  />
                  <Label htmlFor="is_critical" className="text-sm">Critical Path</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="requires_shop_drawings"
                    checked={newItem.requires_shop_drawings}
                    onCheckedChange={(checked) => setNewItem({ ...newItem, requires_shop_drawings: checked })}
                  />
                  <Label htmlFor="requires_shop_drawings" className="text-sm">Requires Shop Drawings</Label>
                </div>
              </div>

              {/* Key Dates */}
              <div className="border-t pt-4 mt-4">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Key Planned Dates</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Enquiry Issue</Label>
                    <Input
                      type="date"
                      value={newItem.enquiry_issue_planned}
                      onChange={(e) => setNewItem({ ...newItem, enquiry_issue_planned: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Tender Return</Label>
                    <Input
                      type="date"
                      value={newItem.tender_return_planned}
                      onChange={(e) => setNewItem({ ...newItem, tender_return_planned: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Appointment</Label>
                    <Input
                      type="date"
                      value={newItem.appointment_planned}
                      onChange={(e) => setNewItem({ ...newItem, appointment_planned: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Delivery</Label>
                    <Input
                      type="date"
                      value={newItem.delivery_planned}
                      onChange={(e) => setNewItem({ ...newItem, delivery_planned: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Installation Complete</Label>
                    <Input
                      type="date"
                      value={newItem.installation_complete_planned}
                      onChange={(e) => setNewItem({ ...newItem, installation_complete_planned: e.target.value })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Notes</Label>
                <Textarea
                  value={newItem.notes}
                  onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
                  placeholder="Additional notes..."
                  className="mt-1 rounded-sm"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                disabled={creating}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
                data-testid="submit-create-btn"
              >
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Item
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : selectedItem ? (
              <>
                <DialogHeader>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0">
                      <DialogTitle className="text-lg sm:text-xl font-barlow font-bold uppercase break-words">
                        {selectedItem.package_name}
                      </DialogTitle>
                    </div>
                    <Badge className={`rounded-sm self-start shrink-0 ${STATUS_COLORS[normalizeStatus(selectedItem.status)] || ''}`}>
                      {normalizeStatus(selectedItem.status)}
                    </Badge>
                  </div>
                </DialogHeader>

                {/* Contractor/Subcontractor Section */}
                <div className="mb-4 p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-600">
                      Subcontractor / Supplier
                    </p>
                    {!editingContractor && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={startEditingContractor}
                        className="h-7 text-xs hover:bg-slate-200"
                        data-testid="edit-contractor-btn"
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    )}
                  </div>
                  
                  {editingContractor ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <Label className="text-xs">Company Name</Label>
                        <Input
                          value={companySearch}
                          onChange={(e) => {
                            setCompanySearch(e.target.value);
                            setContractorName(e.target.value);
                            setShowCompanyDropdown(true);
                          }}
                          onFocus={() => setShowCompanyDropdown(true)}
                          onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                          placeholder="Search or type company name..."
                          className="mt-1 rounded-sm h-8 text-sm"
                          data-testid="contractor-company-search"
                        />
                        {showCompanyDropdown && filteredCompanies.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg max-h-40 overflow-y-auto" data-testid="company-dropdown-list">
                            {filteredCompanies.map((c, i) => (
                              <button
                                key={c.id || i}
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b border-slate-100 last:border-0"
                                onClick={() => selectCompanyFromDropdown(c)}
                                data-testid={`company-option-${i}`}
                              >
                                <p className="font-medium text-slate-800">{c.company_name}</p>
                                <p className="text-[10px] text-slate-400">
                                  {c.contact_person && `${c.contact_person} `}
                                  {c.source === 'crm' ? '(CRM)' : '(Subcontractor)'}
                                  {c.trade && ` - ${c.trade}`}
                                </p>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Contact Person</Label>
                          <Input
                            value={contractorContact}
                            onChange={(e) => setContractorContact(e.target.value)}
                            placeholder="John Smith"
                            className="mt-1 rounded-sm h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Phone</Label>
                          <Input
                            value={contractorPhone}
                            onChange={(e) => setContractorPhone(e.target.value)}
                            placeholder="+27 82 123 4567"
                            className="mt-1 rounded-sm h-8 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Email</Label>
                        <Input
                          value={contractorEmail}
                          onChange={(e) => setContractorEmail(e.target.value)}
                          placeholder="john@abcelectrical.co.za"
                          type="email"
                          className="mt-1 rounded-sm h-8 text-sm"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingContractor(false)}
                          className="h-7 rounded-sm"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveContractor}
                          disabled={savingContractor}
                          className="h-7 rounded-sm bg-blue-600 hover:bg-blue-700"
                        >
                          {savingContractor ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            'Save'
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : selectedItem.subcontractor || selectedItem.supplier ? (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-slate-800">
                        {selectedItem.subcontractor || selectedItem.supplier}
                      </p>
                      {selectedItem.subcontractor_contact && (
                        <p className="text-xs text-slate-600">
                          Contact: {selectedItem.subcontractor_contact}
                        </p>
                      )}
                      {selectedItem.subcontractor_phone && (
                        <p className="text-xs text-slate-600">
                          Phone: {selectedItem.subcontractor_phone}
                        </p>
                      )}
                      {selectedItem.subcontractor_email && (
                        <p className="text-xs text-slate-600">
                          Email: {selectedItem.subcontractor_email}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No subcontractor assigned - click Edit to add</p>
                  )}
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedItem.trade_category && (
                    <Badge className="rounded-sm bg-slate-100 text-slate-700">
                      {TRADE_LABELS[selectedItem.trade_category] || selectedItem.trade_category}
                    </Badge>
                  )}
                  {selectedItem.area && selectedItem.area !== 'general' && (
                    <Badge className="rounded-sm bg-blue-100 text-blue-700">
                      {selectedItem.area.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Badge>
                  )}
                  {selectedItem.source === 'programme_extraction' && (
                    <Badge className="rounded-sm bg-emerald-100 text-emerald-700">
                      <Sparkles className="h-3 w-3 mr-1" /> Auto-Generated
                    </Badge>
                  )}
                  {selectedItem.is_long_lead && (
                    <Badge className="rounded-sm bg-amber-100 text-amber-700">Long Lead</Badge>
                  )}
                  {selectedItem.is_critical && (
                    <Badge className="rounded-sm bg-red-100 text-red-700">Critical Path</Badge>
                  )}
                  {selectedItem.delay_flag && (
                    <Badge className="rounded-sm bg-red-100 text-red-700">
                      Delayed: {selectedItem.delay_days} days - {selectedItem.delay_reason}
                    </Badge>
                  )}
                </div>

                {/* Lead Time Section */}
                <div className="mb-4 p-3 bg-amber-50 rounded-sm border border-amber-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Lead Time</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Input
                        type="number"
                        min="0"
                        value={selectedItem.lead_time_value || ''}
                        onChange={(e) => setSelectedItem(prev => ({ ...prev, lead_time_value: e.target.value ? parseFloat(e.target.value) : null }))}
                        className="h-7 w-16 text-xs rounded-sm"
                        placeholder="0"
                        data-testid="detail-lead-time-value"
                      />
                      <Select
                        value={selectedItem.lead_time_unit || 'weeks'}
                        onValueChange={(val) => setSelectedItem(prev => ({ ...prev, lead_time_unit: val }))}
                      >
                        <SelectTrigger className="h-7 w-20 text-xs rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weeks">weeks</SelectItem>
                          <SelectItem value="days">days</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 rounded-sm text-xs"
                        onClick={async () => {
                          try {
                            await api.put(`/procurement/${selectedItem.id}`, {
                              lead_time_value: selectedItem.lead_time_value,
                              lead_time_unit: selectedItem.lead_time_unit || 'weeks'
                            });
                            // Refresh item to get recalculated dates
                            const updatedItem = await api.get(`/procurement/${selectedItem.id}`);
                            setSelectedItem(updatedItem.data);
                            toast.success(`Lead time saved. Dates recalculated.`);
                            loadData();
                          } catch { toast.error('Failed to save lead time'); }
                        }}
                        data-testid="save-lead-time-btn"
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Shop Drawings Section */}
                <div className="mb-4 p-3 bg-indigo-50 rounded-sm border border-indigo-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-indigo-600">
                      Shop Drawings
                    </p>
                    <div className="flex items-center gap-2">
                      {!selectedItem.linked_shop_drawing && (
                        <button
                          onClick={async () => {
                            const newVal = !selectedItem.requires_shop_drawings;
                            setTogglingShopDrawings(true);
                            try {
                              await api.put(`/procurement/${selectedItem.id}`, { requires_shop_drawings: newVal });
                              setSelectedItem(prev => ({ ...prev, requires_shop_drawings: newVal }));
                              loadData();
                              if (newVal) {
                                setShowShopDrawingConfirm(true);
                              }
                              toast.success(newVal ? 'Shop drawings marked as required' : 'Shop drawings marked as not required');
                            } catch { toast.error('Failed to update'); }
                            finally { setTogglingShopDrawings(false); }
                          }}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                            selectedItem.requires_shop_drawings ? 'bg-indigo-600' : 'bg-slate-300'
                          }`}
                          disabled={togglingShopDrawings}
                          data-testid="toggle-shop-drawings"
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                            selectedItem.requires_shop_drawings ? 'translate-x-4.5 ml-[18px]' : 'translate-x-0.5 ml-[2px]'
                          }`} />
                        </button>
                      )}
                      {selectedItem.linked_shop_drawing ? (
                        <Badge className="rounded-sm bg-green-100 text-green-700 text-xs">Linked</Badge>
                      ) : selectedItem.requires_shop_drawings ? (
                        <Badge className="rounded-sm bg-indigo-100 text-indigo-700 text-xs">Required</Badge>
                      ) : (
                        <Badge className="rounded-sm bg-slate-100 text-slate-500 text-xs">Not Required</Badge>
                      )}
                    </div>
                  </div>
                  
                  {selectedItem.linked_shop_drawing ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-700">
                          {selectedItem.linked_shop_drawing.package_ref} - {selectedItem.linked_shop_drawing.title}
                        </p>
                        <Badge className="mt-1 rounded-sm text-xs">{selectedItem.linked_shop_drawing.status}</Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSyncShopDrawingDates}
                          className="rounded-sm text-xs"
                          data-testid="sync-sd-dates-btn"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" /> Sync Dates
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/projects/${projectId}/shop-drawings?package=${selectedItem.linked_shop_drawing.id}`)}
                          className="rounded-sm text-xs"
                          data-testid="view-shop-drawing-btn"
                        >
                          <Eye className="h-3 w-3 mr-1" /> View
                        </Button>
                      </div>
                    </div>
                  ) : selectedItem.requires_shop_drawings ? (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <p className="text-xs text-slate-500">No shop drawing package linked yet</p>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            handleSearchShopDrawings('');
                            setLinkSDDialogOpen(true);
                          }}
                          className="rounded-sm text-xs"
                          data-testid="link-existing-sd-btn"
                        >
                          Link Existing
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleCreateShopDrawing}
                          className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-xs"
                          data-testid="create-shop-drawing-btn"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Create New
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500">Toggle to mark shop drawings as required</p>
                  )}
                </div>

                {/* Shop Drawing Confirmation Dialog */}
                <Dialog open={showShopDrawingConfirm} onOpenChange={setShowShopDrawingConfirm}>
                  <DialogContent className="max-w-[95vw] sm:max-w-sm">
                    <DialogHeader>
                      <DialogTitle className="text-lg font-bold">Create Shop Drawing Package?</DialogTitle>
                      <DialogDescription className="text-sm text-slate-500">
                        Would you also like to create a draft shop drawing package for <strong>{selectedItem?.package_name}</strong>? This will link the package to this procurement item and notify the team.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setShowShopDrawingConfirm(false)}>
                        Not Now
                      </Button>
                      <Button
                        size="sm"
                        className="rounded-sm bg-indigo-600 hover:bg-indigo-700"
                        onClick={async () => {
                          setShowShopDrawingConfirm(false);
                          await handleCreateShopDrawing();
                        }}
                        data-testid="confirm-create-sd-package"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Yes, Create Package
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Link Existing Shop Drawing Dialog */}
                <Dialog open={linkSDDialogOpen} onOpenChange={setLinkSDDialogOpen}>
                  <DialogContent className="max-w-[95vw] sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-lg font-bold">Link Existing Shop Drawing</DialogTitle>
                      <DialogDescription className="text-sm text-slate-500">
                        Search and link an existing shop drawing package to <strong>{selectedItem?.package_name}</strong>.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <Input
                        value={sdSearchQuery}
                        onChange={(e) => handleSearchShopDrawings(e.target.value)}
                        placeholder="Search by title, ref, or trade..."
                        className="h-8 text-xs"
                        autoFocus
                        data-testid="sd-search-input"
                      />
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {sdSearchResults.length === 0 ? (
                          <p className="text-xs text-slate-400 text-center py-4">No shop drawing packages found</p>
                        ) : (
                          sdSearchResults.map((sd) => (
                            <button
                              key={sd.id}
                              className={`w-full text-left px-3 py-2 rounded-sm border text-sm hover:bg-blue-50 transition-colors ${
                                sd.linked_procurement_id ? 'border-amber-200 bg-amber-50/50' : 'border-slate-200'
                              }`}
                              onClick={() => handleLinkExistingSD(sd.id)}
                              disabled={linkingSD}
                              data-testid={`link-sd-${sd.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-xs text-slate-800">{sd.package_ref} - {sd.title}</p>
                                  <p className="text-[10px] text-slate-400">{sd.trade || '-'}</p>
                                </div>
                                <Badge className="text-[9px] rounded-sm">{sd.status}</Badge>
                              </div>
                              {sd.linked_procurement_id && (
                                <p className="text-[9px] text-amber-600 mt-1">Already linked to another item</p>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setLinkSDDialogOpen(false)}>Cancel</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                {selectedItem.programme_link?.activities?.length > 0 && (
                  <div className="mb-4 p-3 bg-emerald-50 rounded-sm border border-emerald-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2">
                      Linked Programme Activities ({selectedItem.programme_link.activities.length})
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {selectedItem.programme_link.activities.slice(0, 5).map((act, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-slate-600">{act.activity_id} - {act.activity_name}</span>
                          <span className="text-slate-400">{formatDate(act.start_date)}</span>
                        </div>
                      ))}
                      {selectedItem.programme_link.activities.length > 5 && (
                        <p className="text-xs text-slate-400">
                          +{selectedItem.programme_link.activities.length - 5} more activities
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Tabs for phases */}
                <Tabs defaultValue="procurement" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="procurement" className="text-xs">Procurement</TabsTrigger>
                    <TabsTrigger value="shop_drawings" className="text-xs">Shop Drgs</TabsTrigger>
                    <TabsTrigger value="manufacturing" className="text-xs">Manufacturing</TabsTrigger>
                    <TabsTrigger value="delivery" className="text-xs">Delivery</TabsTrigger>
                  </TabsList>

                  <TabsContent value="procurement" className="mt-4 space-y-1">
                    {renderDateCell('procurement_phase', 'enquiry_issue', 'Enquiry Issue')}
                    {renderDateCell('procurement_phase', 'tender_return', 'Tender Return')}
                    {renderDateCell('procurement_phase', 'adjudication', 'Adjudication')}
                    {renderDateCell('procurement_phase', 'appointment', 'Appointment')}
                  </TabsContent>

                  <TabsContent value="shop_drawings" className="mt-4 space-y-1">
                    {selectedItem.linked_shop_drawing ? (
                      <div className="space-y-2">
                        <div className="p-2 bg-indigo-50 rounded-sm border border-indigo-200 mb-3">
                          <p className="text-xs font-bold text-indigo-600 mb-1">
                            Dates managed by Shop Drawing Module
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {selectedItem.linked_shop_drawing.package_ref} - {selectedItem.linked_shop_drawing.title}
                          </p>
                        </div>
                        {/* Submit date - from shop drawing */}
                        <div className="border-b border-slate-100 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">Submit</span>
                            {selectedItem.linked_shop_drawing.sd_submit_actual && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Submitted</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <span className="text-slate-400 block mb-1">Planned</span>
                              <span className="text-slate-700 bg-slate-50 px-2 py-1 rounded inline-block">
                                {formatDate(selectedItem.shop_drawing_phase?.submit?.planned)}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400 block mb-1">Forecast</span>
                              <span className="text-slate-700 bg-slate-50 px-2 py-1 rounded inline-block">
                                {formatDate(selectedItem.shop_drawing_phase?.submit?.forecast)}
                              </span>
                            </div>
                            <div>
                              <span className="text-indigo-500 block mb-1">Actual (from SD Module)</span>
                              <span className="text-indigo-700 font-medium bg-indigo-50 px-2 py-1 rounded inline-block">
                                {formatDate(selectedItem.linked_shop_drawing.sd_submit_actual || selectedItem.shop_drawing_phase?.submit?.actual)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {/* Approval date - from shop drawing */}
                        <div className="border-b border-slate-100 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">Approval</span>
                            {selectedItem.linked_shop_drawing.sd_approval_actual && (
                              <Badge className="bg-green-100 text-green-700 text-xs">Approved</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <span className="text-slate-400 block mb-1">Planned</span>
                              <span className="text-slate-700 bg-slate-50 px-2 py-1 rounded inline-block">
                                {formatDate(selectedItem.shop_drawing_phase?.approval?.planned)}
                              </span>
                            </div>
                            <div>
                              <span className="text-slate-400 block mb-1">Forecast</span>
                              <span className="text-slate-700 bg-slate-50 px-2 py-1 rounded inline-block">
                                {formatDate(selectedItem.shop_drawing_phase?.approval?.forecast)}
                              </span>
                            </div>
                            <div>
                              <span className="text-indigo-500 block mb-1">Actual (from SD Module)</span>
                              <span className="text-indigo-700 font-medium bg-indigo-50 px-2 py-1 rounded inline-block">
                                {formatDate(selectedItem.linked_shop_drawing.sd_approval_actual || selectedItem.shop_drawing_phase?.approval?.actual)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {renderDateCell('shop_drawing_phase', 'revision', 'Revision')}
                        <div className="mt-3 flex justify-end">
                          <Button variant="outline" size="sm" onClick={handleSyncShopDrawingDates} className="text-xs rounded-sm" data-testid="sync-sd-dates-tab-btn">
                            <RefreshCw className="h-3 w-3 mr-1" /> Sync from SD Module
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {renderDateCell('shop_drawing_phase', 'submit', 'Submit')}
                        {renderDateCell('shop_drawing_phase', 'approval', 'Approval')}
                        {renderDateCell('shop_drawing_phase', 'revision', 'Revision')}
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="manufacturing" className="mt-4 space-y-1">
                    {renderDateCell('manufacturing_phase', 'material_order', 'Material Order')}
                    {renderDateCell('manufacturing_phase', 'fabrication_start', 'Fabrication Start')}
                    {renderDateCell('manufacturing_phase', 'fabrication_complete', 'Fabrication Complete')}
                    {renderDateCell('manufacturing_phase', 'factory_inspection', 'Factory Inspection')}
                  </TabsContent>

                  <TabsContent value="delivery" className="mt-4 space-y-1">
                    {renderDateCell('delivery_phase', 'delivery_to_site', 'Delivery to Site')}
                    {renderDateCell('delivery_phase', 'installation_start', 'Installation Start')}
                    {renderDateCell('delivery_phase', 'installation_complete', 'Installation Complete')}
                    {renderDateCell('delivery_phase', 'testing_commissioning', 'Testing & Commissioning')}
                  </TabsContent>
                </Tabs>

                {/* Notes */}
                {selectedItem.notes && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-sm">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Notes</p>
                    <p className="text-sm text-slate-700">{selectedItem.notes}</p>
                  </div>
                )}

                {/* Date Edit Dialog */}
                {editingDate && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-sm border border-blue-200">
                    <p className="text-sm font-bold mb-3">
                      Update {editingDate.activity.replace('_', ' ')} ({editingDate.type})
                    </p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs">Date</Label>
                        <Input
                          type="date"
                          value={dateValue ? dateValue.split('T')[0] : ''}
                          onChange={(e) => setDateValue(e.target.value)}
                          className="mt-1 rounded-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Reason for Change (Optional)</Label>
                        <Input
                          value={dateReason}
                          onChange={(e) => setDateReason(e.target.value)}
                          placeholder="e.g., Programme revision, client delay..."
                          className="mt-1 rounded-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleDateUpdate}
                          className="rounded-sm bg-blue-600 hover:bg-blue-700"
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingDate(null);
                            setDateValue('');
                            setDateReason('');
                          }}
                          className="rounded-sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lead Times Editor Button */}
                {selectedItem.lead_times && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openLeadTimesEditor(selectedItem)}
                      className="rounded-sm w-full"
                    >
                      <Settings2 className="h-4 w-4 mr-2" />
                      Edit Lead Times
                    </Button>
                  </div>
                )}
              </>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Lead Times Editor Dialog */}
        <Dialog open={leadTimesDialogOpen} onOpenChange={setLeadTimesDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                Edit Lead Times
              </DialogTitle>
              <DialogDescription>
                {editingLeadTimes?.package_name} - Adjust lead times (in working days)
              </DialogDescription>
            </DialogHeader>

            {editingLeadTimes && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Enquiry to Tender</Label>
                    <Input
                      type="number"
                      value={editingLeadTimes.lead_times.enquiry_to_tender || 14}
                      onChange={(e) => setEditingLeadTimes({
                        ...editingLeadTimes,
                        lead_times: {...editingLeadTimes.lead_times, enquiry_to_tender: parseInt(e.target.value) || 0}
                      })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tender to Appointment</Label>
                    <Input
                      type="number"
                      value={editingLeadTimes.lead_times.tender_to_appointment || 21}
                      onChange={(e) => setEditingLeadTimes({
                        ...editingLeadTimes,
                        lead_times: {...editingLeadTimes.lead_times, tender_to_appointment: parseInt(e.target.value) || 0}
                      })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Shop Drawings</Label>
                    <Input
                      type="number"
                      value={editingLeadTimes.lead_times.shop_drawings || 21}
                      onChange={(e) => setEditingLeadTimes({
                        ...editingLeadTimes,
                        lead_times: {...editingLeadTimes.lead_times, shop_drawings: parseInt(e.target.value) || 0}
                      })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Manufacturing</Label>
                    <Input
                      type="number"
                      value={editingLeadTimes.lead_times.manufacturing || 45}
                      onChange={(e) => setEditingLeadTimes({
                        ...editingLeadTimes,
                        lead_times: {...editingLeadTimes.lead_times, manufacturing: parseInt(e.target.value) || 0}
                      })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Delivery</Label>
                    <Input
                      type="number"
                      value={editingLeadTimes.lead_times.delivery || 14}
                      onChange={(e) => setEditingLeadTimes({
                        ...editingLeadTimes,
                        lead_times: {...editingLeadTimes.lead_times, delivery: parseInt(e.target.value) || 0}
                      })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Installation</Label>
                    <Input
                      type="number"
                      value={editingLeadTimes.lead_times.installation || 30}
                      onChange={(e) => setEditingLeadTimes({
                        ...editingLeadTimes,
                        lead_times: {...editingLeadTimes.lead_times, installation: parseInt(e.target.value) || 0}
                      })}
                      className="mt-1 rounded-sm"
                    />
                  </div>
                </div>

                <p className="text-xs text-slate-500">
                  Changing lead times will recalculate all procurement dates working backwards from installation start.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setLeadTimesDialogOpen(false);
                  setEditingLeadTimes(null);
                }}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateLeadTimes}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
              >
                Update & Recalculate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark Received Dialog */}
        <Dialog open={markReceivedOpen} onOpenChange={(open) => {
          if (!open) { setMarkReceivedItem(null); setReceivedDate(''); setReceivedNotes(''); setReceivedType('received'); }
          setMarkReceivedOpen(open);
        }}>
          <DialogContent className="max-w-[95vw] sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-barlow font-bold uppercase">
                <CheckCircle2 className="h-5 w-5 inline mr-2 text-green-600" />
                Mark Item Received
              </DialogTitle>
              <DialogDescription>
                {markReceivedItem?.description || 'Record receipt of this procurement item'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs font-bold uppercase">Receipt Type</Label>
                <Select value={receivedType} onValueChange={setReceivedType}>
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Fully Received</SelectItem>
                    <SelectItem value="partial">Partially Received</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Date Received</Label>
                <Input
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                  className="mt-1 rounded-sm"
                  data-testid="procurement-received-date-input"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase">Notes</Label>
                <Textarea
                  value={receivedNotes}
                  onChange={(e) => setReceivedNotes(e.target.value)}
                  placeholder="Any notes about receipt..."
                  className="mt-1 rounded-sm"
                  data-testid="procurement-received-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMarkReceivedOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={handleMarkReceivedProcurement} className="rounded-sm bg-green-600 hover:bg-green-700" data-testid="confirm-procurement-received-btn">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm Received
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Share/Issue Schedule Dialog - RFI-style */}
        <Dialog open={shareDialogOpen} onOpenChange={(open) => {
          setShareDialogOpen(open);
          if (open) loadProjectTeamForShare();
        }}>
          <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <Send className="h-5 w-5" />
                Issue Schedule
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                Send <strong>{selectedSchedule?.schedule_name}</strong> to the selected recipients.
                {selectedSchedule?.linked_subcontractor && (
                  <span className="block mt-1 text-xs text-blue-600">
                    Linked sub-contractor: {selectedSchedule.linked_subcontractor.company_name}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-3">
              {/* Send Type Selector */}
              <div>
                <Label className="text-xs sm:text-sm font-medium">Send Type</Label>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mt-2">
                  <Button
                    type="button"
                    variant={shareSendType === 'external' ? 'default' : 'outline'}
                    className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${shareSendType === 'external' ? 'bg-blue-600' : ''}`}
                    onClick={() => setShareSendType('external')}
                    data-testid="share-type-external"
                  >
                    <Mail className="h-3.5 w-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">External</span>
                  </Button>
                  <Button
                    type="button"
                    variant={shareSendType === 'internal' ? 'default' : 'outline'}
                    className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${shareSendType === 'internal' ? 'bg-blue-600' : ''}`}
                    onClick={() => setShareSendType('internal')}
                    data-testid="share-type-internal"
                  >
                    <Building2 className="h-3.5 w-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">Internal</span>
                  </Button>
                  <Button
                    type="button"
                    variant={shareSendType === 'both' ? 'default' : 'outline'}
                    className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${shareSendType === 'both' ? 'bg-green-600' : ''}`}
                    onClick={() => setShareSendType('both')}
                    data-testid="share-type-both"
                  >
                    <Users className="h-3.5 w-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">Both</span>
                  </Button>
                </div>
                <p className="text-[10px] sm:text-xs text-slate-500 mt-1">
                  {shareSendType === 'external' 
                    ? 'Send to external consultants/sub-contractors' 
                    : shareSendType === 'internal'
                    ? 'Send to internal team members'
                    : 'Send to both internal team and external consultants'}
                </p>
              </div>

              {/* Project Team Members / Consultants */}
              {projectTeamMembers.length > 0 && (
                <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <Label className="text-xs sm:text-sm font-medium mb-2 block">
                    {shareSendType === 'internal' ? 'Internal Team' : shareSendType === 'external' ? 'Consultants & Subcontractors' : 'All Contacts'}
                  </Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {projectTeamMembers
                      .filter(m => {
                        if (shareSendType === 'internal') return m.is_internal;
                        if (shareSendType === 'external') return m.is_consultant || m.is_subcontractor;
                        return true;
                      })
                      .map((m, idx) => {
                        const isAdded = shareRecipients.some(r => r.email === m.email);
                        return (
                          <div key={idx} className="flex items-center justify-between text-xs p-1 rounded hover:bg-white gap-1">
                            <div className="min-w-0 flex-1">
                              <span className="font-medium">{m.name}</span>
                              <span className="text-slate-400 ml-1 text-[10px] hidden sm:inline">{m.email}</span>
                              {m.role && <Badge className="ml-1 text-[8px] sm:text-[9px] h-4 rounded-sm">{m.role}</Badge>}
                            </div>
                            <Button
                              size="sm"
                              variant={isAdded ? 'ghost' : 'outline'}
                              className="h-6 text-[10px] rounded-sm shrink-0"
                              disabled={isAdded}
                              onClick={() => setShareRecipients(prev => [...prev, { name: m.name, email: m.email, role: m.role }])}
                            >
                              {isAdded ? 'Added' : 'Add'}
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Auto-add linked sub-contractor */}
              {selectedSchedule?.linked_subcontractor?.email && !shareRecipients.find(r => r.email === selectedSchedule.linked_subcontractor.email) && (
                <div className="p-2 bg-blue-50 rounded-sm border border-blue-200 text-xs">
                  <p className="text-slate-600 mb-1">Linked Sub-contractor:</p>
                  <div className="flex items-center justify-between gap-1">
                    <span className="truncate">{selectedSchedule.linked_subcontractor.company_name}</span>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-sm shrink-0" onClick={() => {
                      setShareRecipients(prev => [...prev, {
                        name: selectedSchedule.linked_subcontractor.contact_person || selectedSchedule.linked_subcontractor.company_name,
                        email: selectedSchedule.linked_subcontractor.email,
                        role: 'Subcontractor'
                      }]);
                    }}>Add</Button>
                  </div>
                </div>
              )}

              {/* Manual add recipient */}
              <div className="flex flex-col sm:grid sm:grid-cols-3 gap-1.5 sm:gap-2">
                <Input value={shareName} onChange={e => setShareName(e.target.value)} placeholder="Name" className="h-8 text-xs rounded-sm" data-testid="share-name-input" />
                <Input value={shareEmail} onChange={e => setShareEmail(e.target.value)} placeholder="Email" className="h-8 text-xs rounded-sm" type="email" data-testid="share-email-input" />
                <div className="flex gap-1">
                  <Input value={shareRole} onChange={e => setShareRole(e.target.value)} placeholder="Role" className="h-8 text-xs rounded-sm flex-1" data-testid="share-role-input" />
                  <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm px-2" onClick={() => {
                    if (!shareEmail.trim()) return;
                    setShareRecipients(prev => [...prev, { name: shareName, email: shareEmail, role: shareRole }]);
                    setShareName(''); setShareEmail(''); setShareRole('');
                  }} data-testid="share-add-recipient-btn">+</Button>
                </div>
              </div>

              {/* Selected recipients */}
              {shareRecipients.length > 0 && (
                <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <Label className="text-xs sm:text-sm font-medium mb-2 block">Recipients ({shareRecipients.length})</Label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {shareRecipients.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-1 bg-white rounded-sm gap-1">
                        <div className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{r.name || r.email}</span>
                          <span className="text-slate-400 ml-1 hidden sm:inline">{r.name ? r.email : ''}</span>
                          {r.role && <Badge className="ml-1 text-[9px] h-4 rounded-sm">{r.role}</Badge>}
                        </div>
                        <button className="text-red-400 hover:text-red-600 shrink-0" onClick={() => setShareRecipients(prev => prev.filter((_, j) => j !== i))}>
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send email checkbox */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="share_send_email" checked={shareSendEmail}
                  onChange={(e) => setShareSendEmail(e.target.checked)} className="rounded" data-testid="share-send-email-checkbox" />
                <Label htmlFor="share_send_email" className="text-xs sm:text-sm">Open email client with schedule details</Label>
              </div>

              {/* Info panel */}
              <div className="p-2 sm:p-3 bg-blue-50 rounded-sm border border-blue-200">
                <p className="text-xs sm:text-sm text-blue-700 font-medium">What happens when you issue:</p>
                <ul className="text-[10px] sm:text-xs text-blue-600 mt-1 space-y-0.5">
                  <li>• Schedule shared with selected recipients</li>
                  <li>• Email sent with schedule details attached</li>
                  <li>• Activity logged in communication history</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => { setShareDialogOpen(false); setShareRecipients([]); }}>
                Cancel
              </Button>
              <Button
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
                disabled={shareRecipients.length === 0 || sharingInProgress}
                onClick={async () => {
                  if (!selectedSchedule) return;
                  setSharingInProgress(true);
                  try {
                    await api.post(`/projects/${projectId}/schedule/${selectedSchedule.id}/share`, {
                      recipients: shareRecipients.map(r => ({ name: r.name, email: r.email, role: r.role, permission: 'view' })),
                      send_type: shareSendType,
                      send_email: shareSendEmail
                    });
                    if (shareSendEmail) {
                      const toEmails = shareRecipients.map(r => r.email).join(',');
                      const subject = encodeURIComponent(`${selectedSchedule.schedule_name} - Procurement Schedule`);
                      const body = encodeURIComponent(
                        `Dear all,\n\nPlease find the Procurement Schedule details below:\n\n` +
                        `Schedule: ${selectedSchedule.schedule_name}\n` +
                        `Trade: ${selectedSchedule.trade_package || '-'}\n` +
                        `Subcontractor: ${selectedSchedule.linked_subcontractor?.company_name || 'Not assigned'}\n` +
                        `Status: ${selectedSchedule.status}\n` +
                        `Items: ${scheduleItems.length}\n` +
                        `Rev: ${selectedSchedule.revision || 0}\n\n` +
                        `View online: ${window.location.href}\n\n` +
                        `Kind regards`
                      );
                      window.open(`mailto:${toEmails}?subject=${subject}&body=${body}`, '_blank');
                    }
                    toast.success(`Schedule issued to ${shareRecipients.length} recipients`);
                    setShareDialogOpen(false);
                    setShareRecipients([]);
                  } catch { toast.error('Failed to issue schedule'); }
                  finally { setSharingInProgress(false); }
                }}
                data-testid="confirm-share-btn"
              >
                {sharingInProgress ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Issuing...
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5 mr-1" />
                    Issue Schedule ({shareRecipients.length})
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Clean Schedule Dialog */}
        <Dialog open={cleanDialogOpen} onOpenChange={setCleanDialogOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Clean Schedule
              </DialogTitle>
              <DialogDescription>
                AI-powered analysis of <strong>{selectedSchedule?.schedule_name}</strong> to identify items that don't match the <strong>{selectedSchedule?.trade_package || 'specified'}</strong> trade.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {cleanAnalyzing ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
                  <p className="text-sm text-slate-500">Analyzing {scheduleItems.length} items for trade relevance...</p>
                </div>
              ) : cleanFlagged.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">All items match the trade</p>
                  <p className="text-xs text-slate-400">No off-trade items detected</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-amber-700">
                      {cleanFlagged.length} item{cleanFlagged.length !== 1 ? 's' : ''} flagged as off-trade
                    </p>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setCleanSelected(new Set(cleanFlagged.map(f => f.id)))} data-testid="clean-select-all">
                        Select All
                      </Button>
                      <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setCleanSelected(new Set())} data-testid="clean-deselect-all">
                        Deselect All
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1 border rounded-sm">
                    {cleanFlagged.map((item, idx) => (
                      <label
                        key={item.id || `flagged-${idx}`}
                        className={`flex items-start gap-3 p-2.5 cursor-pointer hover:bg-amber-50 transition-colors border-b border-slate-100 last:border-0 ${
                          cleanSelected.has(item.id) ? 'bg-amber-50/60' : ''
                        }`}
                        data-testid={`clean-item-${item.id || idx}`}
                      >
                        <input
                          type="checkbox"
                          checked={cleanSelected.has(item.id)}
                          onChange={() => {
                            setCleanSelected(prev => {
                              const next = new Set(prev);
                              if (next.has(item.id)) next.delete(item.id);
                              else next.add(item.id);
                              return next;
                            });
                          }}
                          className="mt-0.5 rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-800 truncate">
                            #{item.item_no} — {item.description}
                          </p>
                          <p className="text-[10px] text-amber-600 mt-0.5">{item.reason}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="p-2.5 bg-amber-50 rounded-sm border border-amber-200">
                    <p className="text-xs text-amber-700">
                      <strong>Warning:</strong> Selected items will be permanently removed from this schedule
                      {selectedSchedule?.status === 'published' ? ' and any published procurement items' : ''}. 
                      This action cannot be undone.
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-sm" onClick={() => setCleanDialogOpen(false)}>
                Cancel
              </Button>
              {cleanFlagged.length > 0 && (
                <Button
                  className="rounded-sm bg-red-600 hover:bg-red-700"
                  disabled={cleanSelected.size === 0 || cleanRemoving}
                  onClick={handleCleanRemove}
                  data-testid="confirm-clean-btn"
                >
                  {cleanRemoving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Removing...
                    </>
                  ) : (
                    <>
                      <X className="h-3.5 w-3.5 mr-1" />
                      Remove {cleanSelected.size} Item{cleanSelected.size !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Share Published Items Dialog */}
        <Dialog open={sharePublishedOpen} onOpenChange={setSharePublishedOpen}>
          <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <Send className="h-5 w-5" />
                Issue {sharePublishedItems.length === 1 ? 'Package' : `${sharePublishedItems.length} Packages`}
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {sharePublishedItems.length === 1
                  ? <>Send <strong>{sharePublishedItems[0]?.package_name}</strong> to selected recipients.</>
                  : <>Send {sharePublishedItems.length} published packages to selected recipients.</>}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {/* Items being shared */}
              {sharePublishedItems.length > 1 && (
                <div className="p-2 bg-slate-50 rounded-sm border max-h-20 overflow-y-auto">
                  <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Packages ({sharePublishedItems.length})</p>
                  {sharePublishedItems.map((item, idx) => (
                    <p key={idx} className="text-xs text-slate-700 truncate">{item.package_name || item.description}</p>
                  ))}
                </div>
              )}

              {/* Send Type */}
              <div>
                <Label className="text-xs sm:text-sm font-medium">Send Type</Label>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-3 mt-2">
                  <Button type="button" variant={sharePubSendType === 'external' ? 'default' : 'outline'}
                    className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${sharePubSendType === 'external' ? 'bg-blue-600' : ''}`}
                    onClick={() => setSharePubSendType('external')} data-testid="pub-share-type-external">
                    <Mail className="h-3.5 w-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">External</span>
                  </Button>
                  <Button type="button" variant={sharePubSendType === 'internal' ? 'default' : 'outline'}
                    className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${sharePubSendType === 'internal' ? 'bg-blue-600' : ''}`}
                    onClick={() => setSharePubSendType('internal')} data-testid="pub-share-type-internal">
                    <Building2 className="h-3.5 w-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Internal</span>
                  </Button>
                  <Button type="button" variant={sharePubSendType === 'both' ? 'default' : 'outline'}
                    className={`rounded-sm text-xs px-1 sm:px-3 h-8 sm:h-9 ${sharePubSendType === 'both' ? 'bg-green-600' : ''}`}
                    onClick={() => setSharePubSendType('both')} data-testid="pub-share-type-both">
                    <Users className="h-3.5 w-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Both</span>
                  </Button>
                </div>
              </div>

              {/* Project team */}
              {projectTeamMembers.length > 0 && (
                <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <Label className="text-xs sm:text-sm font-medium mb-2 block">
                    {sharePubSendType === 'internal' ? 'Internal Team' : sharePubSendType === 'external' ? 'Consultants & Subcontractors' : 'All Contacts'}
                  </Label>
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {projectTeamMembers
                      .filter(m => {
                        if (sharePubSendType === 'internal') return m.is_internal;
                        if (sharePubSendType === 'external') return m.is_consultant || m.is_subcontractor;
                        return true;
                      })
                      .map((m, idx) => {
                        const isAdded = sharePubRecipients.some(r => r.email === m.email);
                        return (
                          <div key={idx} className="flex items-center justify-between text-xs p-1 rounded hover:bg-white gap-1">
                            <div className="min-w-0 flex-1">
                              <span className="font-medium">{m.name}</span>
                              <span className="text-slate-400 ml-1 text-[10px] hidden sm:inline">{m.email}</span>
                              {m.role && <Badge className="ml-1 text-[8px] sm:text-[9px] h-4 rounded-sm">{m.role}</Badge>}
                            </div>
                            <Button size="sm" variant={isAdded ? 'ghost' : 'outline'} className="h-6 text-[10px] rounded-sm shrink-0" disabled={isAdded}
                              onClick={() => setSharePubRecipients(prev => [...prev, { name: m.name, email: m.email, role: m.role }])}>
                              {isAdded ? 'Added' : 'Add'}
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Manual add */}
              <div className="flex flex-col sm:grid sm:grid-cols-3 gap-1.5 sm:gap-2">
                <Input value={sharePubName} onChange={e => setSharePubName(e.target.value)} placeholder="Name" className="h-8 text-xs rounded-sm" data-testid="pub-share-name" />
                <Input value={sharePubEmail} onChange={e => setSharePubEmail(e.target.value)} placeholder="Email" className="h-8 text-xs rounded-sm" type="email" data-testid="pub-share-email" />
                <div className="flex gap-1">
                  <Input value={sharePubRole} onChange={e => setSharePubRole(e.target.value)} placeholder="Role" className="h-8 text-xs rounded-sm flex-1" data-testid="pub-share-role" />
                  <Button size="sm" variant="outline" className="h-8 text-xs rounded-sm px-2" onClick={() => {
                    if (!sharePubEmail.trim()) return;
                    setSharePubRecipients(prev => [...prev, { name: sharePubName, email: sharePubEmail, role: sharePubRole }]);
                    setSharePubName(''); setSharePubEmail(''); setSharePubRole('');
                  }} data-testid="pub-share-add-btn">+</Button>
                </div>
              </div>

              {/* Selected recipients */}
              {sharePubRecipients.length > 0 && (
                <div className="p-2 sm:p-3 bg-slate-50 rounded-sm border">
                  <Label className="text-xs sm:text-sm font-medium mb-2 block">Recipients ({sharePubRecipients.length})</Label>
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {sharePubRecipients.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-1 bg-white rounded-sm gap-1">
                        <div className="min-w-0 flex-1 truncate">
                          <span className="font-medium">{r.name || r.email}</span>
                          <span className="text-slate-400 ml-1 hidden sm:inline">{r.name ? r.email : ''}</span>
                          {r.role && <Badge className="ml-1 text-[9px] h-4 rounded-sm">{r.role}</Badge>}
                        </div>
                        <button className="text-red-400 hover:text-red-600 shrink-0" onClick={() => setSharePubRecipients(prev => prev.filter((_, j) => j !== i))}>
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send email */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="pub_send_email" checked={sharePubSendEmail} onChange={e => setSharePubSendEmail(e.target.checked)} className="rounded" />
                <Label htmlFor="pub_send_email" className="text-xs sm:text-sm">Open email client with package details</Label>
              </div>

              {/* Info */}
              <div className="p-2 sm:p-3 bg-blue-50 rounded-sm border border-blue-200">
                <p className="text-xs sm:text-sm text-blue-700 font-medium">What happens when you issue:</p>
                <ul className="text-[10px] sm:text-xs text-blue-600 mt-1 space-y-0.5">
                  <li>• Package details shared with selected recipients</li>
                  <li>• Email sent with schedule summary</li>
                  <li>• Share action logged on each package</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-sm text-xs sm:text-sm" onClick={() => setSharePublishedOpen(false)}>Cancel</Button>
              <Button className="rounded-sm bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm" disabled={sharePubRecipients.length === 0 || sharePubInProgress}
                onClick={handleSharePublished} data-testid="confirm-pub-share-btn">
                {sharePubInProgress ? (
                  <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Issuing...</>
                ) : (
                  <><Send className="h-3.5 w-3.5 mr-1" /> Issue ({sharePubRecipients.length})</>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default ProcurementSchedulePage;
